import { signal, untracked } from "@preact/signals-core";
import {
  assetsListSignal,
  allCustomersSignal,
  sitesSignal,
  pageTypesSignal,
  getAuthHeaders,
  refreshAssets,
  initAssetsSignals,
  deviceModelsSignal,
} from "./assets";
import { consumablesSignal, refreshConsumables } from "./device_models";
import { escapeHtml } from "../utils/sanitize";
import { components } from "../api/v1";
import { savePendingRequest, processOfflineQueue } from "../utils/offline";
import {
  formatDateTime,
  formatDuration,
  formatDurationWords,
} from "../utils/formatters";
import { showToast } from "../utils/toast";
import { userProfileSignal } from "../store/auth";
import { forceRebuildShell } from "../main";
import {
  deriveConsoleSessionView,
  getSafeDurationSeconds,
  isRecord,
  parseSessionTimestamp,
  type ConsoleSessionState,
  type ConsoleSessionView,
} from "./ticket-console-session";

type Ticket = components["schemas"]["Ticket"];
type ServiceSession = components["schemas"]["ServiceSession"];
type PartRecommendation = components["schemas"]["PartRecommendation"];
type ReadingLog = components["schemas"]["ReadingLog"];
type Asset = components["schemas"]["Asset"];
type DeviceModel = components["schemas"]["DeviceModel"];
type Site = components["schemas"]["Site"];
type User = components["schemas"]["User"];

type AutocompleteItem = { id: string; label: string };
type OpmAutocompleteElement = HTMLElement & {
  items: AutocompleteItem[];
  selectedId: string | null;
  query: string;
  clear: () => void;
};

function getOpmAutocomplete(id: string): OpmAutocompleteElement | null {
  const element = document.getElementById(id);
  return element instanceof HTMLElement ? element as OpmAutocompleteElement : null;
}

type MachineHistoryItem = {
  ticket: Ticket;
  timestamp: string;
  kind: "session" | "part";
  session?: ServiceSession;
  part?: PartRecommendation;
};

interface PrinterDetailsReview {
  updates: Record<string, string>;
  summaryLines: string[];
  bannerMessage: string;
}

// ─── Signals ───────────────────────────────────────────────────────────────────

export const activeConsoleTicketIdSignal = signal<string | null>(null);
export const activeConsoleTicketSignal = signal<Ticket | null>(null);
export const activeConsoleSessionSignal = signal<ServiceSession | null>(null);
export const currentTechnicianSessionSignal = signal<ServiceSession | null>(null);
export const consoleSessionStateSignal = signal<ConsoleSessionState>("loading");
export const consoleSessionLastVerifiedSignal = signal<string | null>(null);
export const consoleSessionViewSignal = signal<ConsoleSessionView>({
  state: "loading",
  session: null,
  activeSession: null,
  elapsedSeconds: null,
  startedAtMs: null,
  message: null,
});
export const stopwatchTimeSignal = signal<number>(0);
export const recommendedPartsSignal = signal<PartRecommendation[]>([]);
export const consoleSessionsSignal = signal<ServiceSession[]>([]);
export const consoleHistorySignal = signal<MachineHistoryItem[]>([]);
export const consoleHistoryVisibleCountSignal = signal<number>(12);
export const consoleHistoryLoadingSignal = signal<boolean>(false);
export const consoleHistoryErrorSignal = signal<string | null>(null);
export const consoleUnsavedWorkSignal = signal<boolean>(false);
export const uploadedPhotosSignal = signal<string[]>([]);
export const manualEntrySignal = signal<boolean>(false);
export const consoleActiveTabSignal = signal<"repair" | "closeout">("repair");
export const capturedGpsSignal = signal<{ lat: number; lng: number } | null>(
  null,
);
export const lastAssetReadingSignal = signal<ReadingLog | null>(null);
export const editingRecommendationIdSignal = signal<string | null>(null);
export const consoleUsersMapSignal = signal<Map<string, User>>(new Map());

// ─── Local Variables ───────────────────────────────────────────────────────────

let stopwatchInterval: ReturnType<typeof setInterval> | null = null;
let selectedConsumableId: string | null = null;
let pendingRecommendationCancelId: string | null = null;
let pendingConsoleNavigationRoute: string | null = null;
let consoleBeforeUnloadHandler: ((e: BeforeUnloadEvent) => void) | null = null;
let historyLoadSeq = 0;
let serverClockOffsetMs = 0;
let sessionRefreshSequence = 0;

// ─── Helpers ───────────────────────────────────────────────────────────────────

async function getApiErrorMessage(response: Response, fallback: string): Promise<string> {
  const payload: unknown = await response.json().catch(() => null);
  if (isRecord(payload)) {
    if (typeof payload.message === "string") return payload.message;
    if (typeof payload.error === "string") return payload.error;
  }
  return fallback;
}

function setConsoleSessionView(view: ConsoleSessionView): void {
  consoleSessionViewSignal.value = view;
  consoleSessionStateSignal.value = view.state;
  activeConsoleSessionSignal.value = view.session;
  currentTechnicianSessionSignal.value = view.activeSession;
  if (view.elapsedSeconds !== null) stopwatchTimeSignal.value = view.elapsedSeconds;
}

function startStopwatchLoop(startedAtMs: number) {
  if (!Number.isFinite(startedAtMs)) return;
  if (stopwatchInterval) clearInterval(stopwatchInterval);

  const updateDisplay = () => {
    const elapsedMs = Date.now() + serverClockOffsetMs - startedAtMs;
    const elapsedSecs = Number.isFinite(elapsedMs) ? Math.max(0, Math.floor(elapsedMs / 1000)) : 0;
    untracked(() => {
      stopwatchTimeSignal.value = elapsedSecs;
    });
    const display = document.getElementById("console-stopwatch-display");
    if (display) display.textContent = formatDuration(elapsedSecs);
  };

  updateDisplay();
  stopwatchInterval = setInterval(updateDisplay, 1000);
}

function clearLegacySessionCache(): void {
  localStorage.removeItem("opm_active_session_ticket_id");
  localStorage.removeItem("opm_active_session_id");
  localStorage.removeItem("opm_active_session_start");
}

function stopStopwatchLoop(resetDisplay = true) {
  if (stopwatchInterval) {
    clearInterval(stopwatchInterval);
    stopwatchInterval = null;
  }
  if (resetDisplay) stopwatchTimeSignal.value = 0;
}

function openModal(modalId: string) {
  const modal = document.getElementById(modalId) as
    | { showOverlay?: () => void }
    | null;
  if (modal && typeof modal.showOverlay === "function") {
    modal.showOverlay();
  }
}

function closeModal(modalId: string) {
  const modal = document.getElementById(modalId) as
    | { hideOverlay?: () => void }
    | null;
  if (modal && typeof modal.hideOverlay === "function") {
    modal.hideOverlay();
  }
}

function getDeviceModelLabel(modelId: string | null | undefined): string {
  if (!modelId) return "—";
  const model = (deviceModelsSignal.value || []).find(
    (item: DeviceModel) => item.id === modelId,
  );
  if (!model) return modelId;
  return `${model.vendor} ${model.modelName}`.trim();
}

function configureConsoleAutocompletes(ticket: Ticket): void {
  const modelItems: AutocompleteItem[] = (deviceModelsSignal.value || []).map((model: DeviceModel) => ({
    id: model.id,
    label: `${model.vendor} ${model.modelName}`.trim(),
  }));
  const modelAutocomplete = getOpmAutocomplete("console-override-model");
  if (modelAutocomplete) {
    modelAutocomplete.items = modelItems;
    const asset = (assetsListSignal.value || []).find((item: Asset) => item.id === ticket.assetId);
    modelAutocomplete.selectedId = asset?.modelId || null;
  }
  const rawModelAutocomplete = getOpmAutocomplete("console-override-model-raw");
  if (rawModelAutocomplete) {
    const rawModel = (ticket.modelNameRaw || "").trim();
    rawModelAutocomplete.items = rawModel ? [...modelItems, { id: "__raw_model__", label: rawModel }] : modelItems;
    rawModelAutocomplete.selectedId = rawModel ? "__raw_model__" : null;
  }
  const assetAutocomplete = getOpmAutocomplete("select-link-asset");
  if (assetAutocomplete) {
    const items = (assetsListSignal.value || [])
      .filter((asset: Asset) => asset.customerId === ticket.customerId && (!ticket.siteId || asset.siteId === ticket.siteId))
      .map((asset: Asset) => {
        const model = (deviceModelsSignal.value || []).find((item: DeviceModel) => item.id === asset.modelId);
        return { id: asset.id, label: `${asset.serialNumber} · ${model ? `${model.vendor} ${model.modelName}` : "Unknown model"}` };
      });
    assetAutocomplete.items = items;
    assetAutocomplete.selectedId = null;
  }
}

function getPartStatusTone(status: string): string {
  switch (status) {
    case "requested":
      return "attention";
    case "manager_approved":
    case "admin_approved":
    case "installed":
      return "success";
    case "manager_rejected":
    case "admin_rejected":
      return "critical";
    case "dispatched":
      return "info";
    default:
      return "info";
  }
}

function getPartStatusLabel(status: string): string {
  switch (status) {
    case "requested":
      return "Requested";
    case "manager_approved":
      return "Manager approved";
    case "manager_rejected":
      return "Manager rejected";
    case "admin_approved":
      return "Admin approved";
    case "admin_rejected":
      return "Admin rejected";
    case "dispatched":
      return "Dispatched";
    case "installed":
      return "Installed";
    default:
      return status;
  }
}

function getPartActionLabel(status: string): string {
  switch (status) {
    case "requested":
      return "Requested";
    case "manager_approved":
    case "admin_approved":
      return "Approved";
    case "manager_rejected":
    case "admin_rejected":
      return "Canceled";
    case "dispatched":
      return "Dispatched";
    case "installed":
      return "Installed";
    default:
      return status;
  }
}

function renderAssetHistoryTimeline() {
  const container = document.getElementById("console-asset-history-list");
  const emptyState = document.getElementById("console-asset-history-empty");
  const loadMoreBtn = document.getElementById("btn-console-history-load-more");
  if (!container || !emptyState) return;

  if (consoleHistoryLoadingSignal.value) {
    emptyState.style.display = "block";
    emptyState.innerHTML =
      '<s-stack gap="small" alignment="center"><s-spinner size="small"></s-spinner><s-text tone="subdued">Loading machine history…</s-text></s-stack>';
    container.innerHTML = "";
    if (loadMoreBtn) loadMoreBtn.style.display = "none";
    return;
  }

  if (consoleHistoryErrorSignal.value) {
    emptyState.style.display = "block";
    emptyState.innerHTML = `
      <s-banner tone="critical" title="History unavailable">
        <s-paragraph>${escapeHtml(consoleHistoryErrorSignal.value)}</s-paragraph>
      </s-banner>
    `;
    container.innerHTML = "";
    if (loadMoreBtn) loadMoreBtn.style.display = "none";
    return;
  }

  const entries = consoleHistorySignal.value;
  const consumables = consumablesSignal.value || [];
  if (entries.length === 0) {
    emptyState.style.display = "block";
    emptyState.innerHTML =
      '<s-text tone="subdued" style="font-style:italic;">No machine history found for this ticket yet.</s-text>';
    container.innerHTML = "";
    if (loadMoreBtn) loadMoreBtn.style.display = "none";
    return;
  }

  emptyState.style.display = "none";
  const visibleCount = Math.max(12, consoleHistoryVisibleCountSignal.value);
  const sliceStart = Math.max(0, entries.length - visibleCount);
  const visibleEntries = entries.slice(sliceStart);

  container.innerHTML = visibleEntries
    .map((entry) => {
      const ticketLabel = escapeHtml(entry.ticket.ticketNumber);
      const timestamp = escapeHtml(formatDateTime(entry.timestamp));
      const statusTone = entry.kind === "session" ? "success" : getPartStatusTone(entry.part?.status || "requested");
      let headline = "";
      if (entry.kind === "session") {
        const techId = entry.session?.technicianId;
        const user = techId ? consoleUsersMapSignal.peek().get(techId) : null;
        const techStr = user ? ` by ${user.email}` : "";
        if (entry.session?.endedAt) {
          headline = `Service visit completed${techStr}`;
        } else {
          headline = `Service visit started${techStr}`;
        }
      } else {
        headline = `${getPartActionLabel(entry.part?.status || "requested")} part request`;
      }
      const title = escapeHtml(headline);
      const ticketMeta = [
        entry.ticket.priority ? entry.ticket.priority.toUpperCase() : "",
        entry.ticket.status ? entry.ticket.status.replace(/_/g, " ") : "",
      ].filter(Boolean).join(" · ");
      const consumable = entry.part
        ? consumables.find((item) => item.id === entry.part?.consumableId)
        : null;
      const partName = consumable ? consumable.name : (entry.part?.consumableId || "Unknown part");
      const subtitle = entry.kind === "session"
        ? [
          `Duration: ${formatDurationWords(entry.session?.startedAt || entry.timestamp, entry.session?.endedAt || null)}`,
          entry.session?.isFinal ? "Marked final" : "Open visit",
          entry.session?.photoUrls && entry.session.photoUrls.length > 0 ? `${entry.session.photoUrls.length} photo(s)` : "",
          entry.session?.summary || "",
        ].filter(Boolean).join(" · ")
        : [
          `Part: ${partName}`,
          entry.part?.quantity ? `Qty: ${entry.part.quantity}` : "",
          `Status: ${getPartStatusLabel(entry.part?.status || "requested")}`,
        ].filter(Boolean).join(" · ");

      return `
      <div style="display:grid;grid-template-columns:1.25rem 1fr;gap:0.75rem;align-items:start;">
        <div style="display:flex;justify-content:center;">
          <div style="width:0.85rem;height:0.85rem;border-radius:999px;background:var(--p-color-bg-${statusTone}, #008060);margin-top:0.4rem;"></div>
        </div>
        <s-box padding="base" background="surface" border="base" borderRadius="base">
          <s-stack gap="extraSmall">
            <s-stack direction="inline" gap="small" alignment="center">
              <s-badge tone="${statusTone}">${entry.kind === "session" ? "Service Visit" : getPartStatusLabel(entry.part?.status || "requested")}</s-badge>
              <s-text style="font-weight:600;">${title}</s-text>
            </s-stack>
            <s-text tone="subdued" style="font-size:0.8rem;">Ticket ${ticketLabel}${ticketMeta ? ` · ${escapeHtml(ticketMeta)}` : ""} · ${timestamp}</s-text>
            ${subtitle ? `<s-text style="font-size:0.875rem;">${escapeHtml(subtitle)}</s-text>` : ""}
          </s-stack>
        </s-box>
      </div>
    `;
    })
    .join("");

  if (loadMoreBtn) {
    loadMoreBtn.style.display = entries.length > visibleCount ? "inline-flex" : "none";
  }
}

let _bypassUnsavedWork = false;

export function hasUnsavedConsoleWork(): boolean {
  if (_bypassUnsavedWork) return false;
  const summaryEl = document.getElementById("console-summary") as HTMLTextAreaElement | null;
  const manualEntryEl = document.getElementById("checkbox-log-manual") as HTMLInputElement | null;
  const manualDateEl = document.getElementById("input-manual-date") as HTMLInputElement | null;
  const manualStartEl = document.getElementById("input-manual-start") as HTMLInputElement | null;
  const manualEndEl = document.getElementById("input-manual-end") as HTMLInputElement | null;
  const isFinalEl = document.getElementById("checkbox-is-final") as HTMLInputElement | null;
  const mResetEl = document.getElementById("checkbox-maintenance-reset") as HTMLInputElement | null;
  const counterInputs = (pageTypesSignal.value || []).map((pt) => document.getElementById(`input-counter-${pt.id}`) as HTMLInputElement | null);

  if (summaryEl?.value.trim()) return true;
  if (uploadedPhotosSignal.value.length > 0) return true;
  if (manualEntryEl?.checked) return true;
  if (manualDateEl?.value || manualStartEl?.value || manualEndEl?.value) return true;
  if (isFinalEl?.checked) return true;
  if (mResetEl?.checked) return true;
  return counterInputs.some((input) => !!input?.value.trim());
}

function syncConsoleUnsavedState(): void {
  consoleUnsavedWorkSignal.value = hasUnsavedConsoleWork();
}

function canUseCloseoutTab(): boolean {
	return activeConsoleSessionSignal.value?.status === "awaiting_closeout" || manualEntrySignal.value;
}

function setConsoleActiveTab(tab: "repair" | "closeout"): void {
  if (tab === "closeout" && !canUseCloseoutTab()) {
    consoleActiveTabSignal.value = "repair";
  } else {
    consoleActiveTabSignal.value = tab;
  }
  syncConsoleTabState();
}

function syncConsoleTabState(): void {
  const isCloseoutEnabled = canUseCloseoutTab();
  if (!isCloseoutEnabled && consoleActiveTabSignal.value === "closeout") {
    consoleActiveTabSignal.value = "repair";
  }

  const activeTab = consoleActiveTabSignal.value;
  const repairPanel = document.getElementById("tab-content-repair");
  const closeoutPanel = document.getElementById("tab-content-closeout");
  const repairButton = document.getElementById("tab-btn-repair");
  const closeoutButton = document.getElementById("tab-btn-closeout");

  if (repairPanel) repairPanel.style.display = activeTab === "repair" ? "block" : "none";
  if (closeoutPanel) closeoutPanel.style.display = activeTab === "closeout" ? "block" : "none";

  if (repairButton) {
    repairButton.setAttribute("variant", activeTab === "repair" ? "primary" : "plain");
    repairButton.setAttribute("aria-selected", String(activeTab === "repair"));
  }
  if (closeoutButton) {
    closeoutButton.setAttribute("variant", activeTab === "closeout" ? "primary" : "plain");
    closeoutButton.setAttribute("aria-selected", String(activeTab === "closeout"));
    if (isCloseoutEnabled) {
      closeoutButton.removeAttribute("disabled");
    } else {
      closeoutButton.setAttribute("disabled", "true");
    }
  }
}

function openConsoleLeavePrompt(nextRoute: string): void {
  pendingConsoleNavigationRoute = nextRoute;
  const nextRouteLabel = document.getElementById("console-unsaved-work-next-route");
  if (nextRouteLabel) {
    nextRouteLabel.textContent = nextRoute;
  }
  openModal("console-unsaved-work-modal");
}

function clearConsoleLeavePrompt(): void {
  pendingConsoleNavigationRoute = null;
  const nextRouteLabel = document.getElementById("console-unsaved-work-next-route");
  if (nextRouteLabel) {
    nextRouteLabel.textContent = "";
  }
}

export function promptTicketConsoleLeave(nextRoute: string): void {
  openConsoleLeavePrompt(nextRoute);
}

function buildPrinterDetailsReview(ticket: Ticket): PrinterDetailsReview | null {
  if (ticket.assetId) {
    const asset = (assetsListSignal.value || []).find(
      (item: Asset) => item.id === ticket.assetId,
    );
    const modelAutocomplete = getOpmAutocomplete("console-override-model");
    const serialField = document.getElementById(
      "console-override-serial",
    ) as HTMLInputElement | null;
    const nextModelId = modelAutocomplete?.selectedId || "";
    const nextSerial = serialField?.value.trim() || "";
    const updates: Record<string, string> = {};
    const summaryLines: string[] = [];

    const currentModelLabel = getDeviceModelLabel(asset?.modelId || null);
    const nextModelLabel = getDeviceModelLabel(nextModelId || null);
    if (nextModelId && nextModelId !== asset?.modelId) {
      updates.modelId = nextModelId;
      summaryLines.push(`Model: ${currentModelLabel} → ${nextModelLabel}`);
    }

    const currentSerial = (asset?.serialNumber || "").trim();
    if (nextSerial && nextSerial !== currentSerial) {
      updates.serialNumber = nextSerial;
      summaryLines.push(`Serial: ${currentSerial || "—"} → ${nextSerial}`);
    }

    if (summaryLines.length === 0) {
      return null;
    }

    return {
      updates,
      summaryLines,
      bannerMessage: "Are you sure you want to save these printer detail changes?",
    };
  }

  const modelAutocomplete = getOpmAutocomplete("console-override-model-raw");
  const serialField = document.getElementById(
    "console-override-serial-raw",
  ) as HTMLInputElement | null;
  const nextModelRaw = modelAutocomplete?.query.trim() || "";
  const nextSerialRaw = serialField?.value.trim() || "";
  const updates: Record<string, string> = {};
  const summaryLines: string[] = [];

  if (nextModelRaw && nextModelRaw !== (ticket.modelNameRaw || "").trim()) {
    updates.modelNameRaw = nextModelRaw;
    summaryLines.push(
      `Model: ${(ticket.modelNameRaw || "—").trim() || "—"} → ${nextModelRaw}`,
    );
  }

  if (nextSerialRaw && nextSerialRaw !== (ticket.serialNumberRaw || "").trim()) {
    updates.serialNumberRaw = nextSerialRaw;
    summaryLines.push(
      `Serial: ${(ticket.serialNumberRaw || "—").trim() || "—"} → ${nextSerialRaw}`,
    );
  }

  if (summaryLines.length === 0) {
    return null;
  }

  return {
    updates,
    summaryLines,
    bannerMessage: "Are you sure you want to save these printer detail changes?",
  };
}

async function fetchMachineHistory(): Promise<void> {
  const ticket = activeConsoleTicketSignal.peek();
  if (!ticket) return;

  const loadSeq = ++historyLoadSeq;
  consoleHistoryLoadingSignal.value = true;
  consoleHistoryErrorSignal.value = null;
  consoleHistoryVisibleCountSignal.value = 12;

  try {
    const h = getAuthHeaders();
    const ticketsToInspect: Ticket[] = [ticket];

    if (ticket.assetId) {
      const res = await fetch(
        `/api/v1/tickets?assetId=${ticket.assetId}&page=1&pageSize=100`,
        { headers: h },
      );
      if (res.ok) {
        const assetTickets = (await res.json()) as Ticket[];
        const uniqueTickets = new Map<string, Ticket>();
        [ticket, ...assetTickets].forEach((entry) => {
          uniqueTickets.set(entry.id, entry);
        });
        ticketsToInspect.splice(
          0,
          ticketsToInspect.length,
          ...Array.from(uniqueTickets.values()),
        );
        ticketsToInspect.sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
      }
    }

    const timelineEntries: MachineHistoryItem[] = [];
    for (const entryTicket of ticketsToInspect) {
      const [sessionsRes, partsRes] = await Promise.all([
        fetch(`/api/v1/tickets/${entryTicket.id}/sessions`, { headers: h }),
        fetch(`/api/v1/parts-recommendations?ticketId=${entryTicket.id}`, {
          headers: h,
        }),
      ]);

      if (sessionsRes.ok) {
        const sessions = (await sessionsRes.json()) as ServiceSession[];
        sessions.forEach((session) => {
          timelineEntries.push({
            ticket: entryTicket,
            timestamp: session.endedAt || session.startedAt,
            kind: "session",
            session,
          });
        });
      }

      if (partsRes.ok) {
        const parts = (await partsRes.json()) as PartRecommendation[];
        parts.forEach((part) => {
          timelineEntries.push({
            ticket: entryTicket,
            timestamp: part.updatedAt || part.createdAt,
            kind: "part",
            part,
          });
        });
      }
    }

    timelineEntries.sort((a, b) => {
      const left = new Date(a.timestamp).getTime();
      const right = new Date(b.timestamp).getTime();
      return left - right;
    });

    if (loadSeq === historyLoadSeq) {
      consoleHistorySignal.value = timelineEntries;
    }
  } catch (err) {
    console.error("Failed to fetch console history", err);
    if (loadSeq === historyLoadSeq) {
      consoleHistoryErrorSignal.value =
        "Unable to load service history for this ticket right now.";
      consoleHistorySignal.value = [];
    }
  } finally {
    if (loadSeq === historyLoadSeq) {
      consoleHistoryLoadingSignal.value = false;
      renderAssetHistoryTimeline();
    }
  }
}

/**
 * Teardown hook called by the router before replacing the console page DOM.
 * - Stops any active stopwatch interval to prevent timer leaks.
 * - Caches in-progress diagnostic description to sessionStorage for soft recovery.
 */
export function unmountTicketConsolePage(): void {
  _bypassUnsavedWork = false;
  // 1. Stop stopwatch to release the setInterval handle
  if (stopwatchInterval) {
    clearInterval(stopwatchInterval);
    stopwatchInterval = null;
  }

  // 2. Remove window online sync observer
  window.removeEventListener("online", handleOnlineSync);

  if (consoleBeforeUnloadHandler) {
    window.removeEventListener("beforeunload", consoleBeforeUnloadHandler);
    consoleBeforeUnloadHandler = null;
  }
  pendingConsoleNavigationRoute = null;
  consoleUnsavedWorkSignal.value = false;

  // 3. Cache any in-progress diagnostic description text
  try {
    const diagInput = document.getElementById(
      "session-diagnosis",
    ) as HTMLInputElement | null;
    if (diagInput && diagInput.value.trim()) {
      sessionStorage.setItem("console_draft_diagnosis", diagInput.value.trim());
    } else {
      sessionStorage.removeItem("console_draft_diagnosis");
    }
  } catch (e) {
    // sessionStorage unavailable
  }
}

interface ConsoleDraft {
  summary?: string;
  counters?: Record<string, string>;
  isFinal?: boolean;
  isMaintenanceReset?: boolean;
  manualEntry?: boolean;
  manualDate?: string;
  manualStart?: string;
  manualEnd?: string;
}

function saveConsoleDraft(ticketId: string) {
  const summaryEl = document.getElementById("console-summary") as any;
  const isFinalEl = document.getElementById("checkbox-is-final") as any;
  const mResetEl = document.getElementById("checkbox-maintenance-reset") as any;
  const manualEntryEl = document.getElementById(
    "checkbox-log-manual",
  ) as HTMLInputElement;
  const manualDateEl = document.getElementById(
    "input-manual-date",
  ) as HTMLInputElement;
  const manualStartEl = document.getElementById(
    "input-manual-start",
  ) as HTMLInputElement;
  const manualEndEl = document.getElementById(
    "input-manual-end",
  ) as HTMLInputElement;

  const counters: Record<string, string> = {};
  const pageTypes = pageTypesSignal.value || [];
  pageTypes.forEach((pt) => {
    const input = document.getElementById(`input-counter-${pt.id}`) as any;
    if (input && input.value) {
      counters[pt.id] = input.value;
    }
  });

  const draft: ConsoleDraft = {
    summary: summaryEl ? summaryEl.value : "",
    isFinal: isFinalEl ? isFinalEl.checked : false,
    isMaintenanceReset: mResetEl ? mResetEl.checked : false,
    manualEntry: manualEntryEl ? manualEntryEl.checked : false,
    manualDate: manualDateEl ? manualDateEl.value : "",
    manualStart: manualStartEl ? manualStartEl.value : "",
    manualEnd: manualEndEl ? manualEndEl.value : "",
    counters,
  };

  localStorage.setItem(`opm_draft_${ticketId}`, JSON.stringify(draft));
  syncConsoleUnsavedState();
}

function loadConsoleDraft(ticketId: string) {
  const draftStr = localStorage.getItem(`opm_draft_${ticketId}`);
  if (!draftStr) return;

  try {
    const draft: ConsoleDraft = JSON.parse(draftStr);

    const summaryEl = document.getElementById("console-summary") as any;
    if (summaryEl && draft.summary !== undefined) {
      summaryEl.value = draft.summary;
    }

    const isFinalEl = document.getElementById("checkbox-is-final") as any;
    if (isFinalEl && draft.isFinal !== undefined) {
      isFinalEl.checked = draft.isFinal;
    }

    const mResetEl = document.getElementById(
      "checkbox-maintenance-reset",
    ) as any;
    if (mResetEl && draft.isMaintenanceReset !== undefined) {
      mResetEl.checked = draft.isMaintenanceReset;
    }

    const manualEntryEl = document.getElementById(
      "checkbox-log-manual",
    ) as HTMLInputElement;
    const hasServerCloseout =
      activeConsoleSessionSignal.value?.status === "awaiting_closeout";
    const shouldRestoreManualEntry = !hasServerCloseout && draft.manualEntry === true;
    if (manualEntryEl && draft.manualEntry !== undefined) {
      manualEntryEl.checked = shouldRestoreManualEntry;
      manualEntrySignal.value = shouldRestoreManualEntry;

      const stopwatchDisplay = document.getElementById("stopwatch-container");
      const timePickers = document.getElementById("manual-time-pickers");
      const closeoutForm = document.getElementById("visit-closeout-form");
      const inactiveBanner = document.getElementById("visit-inactive-banner");
      const durationDisplay = document.getElementById(
        "manual-duration-display",
      );

      if (stopwatchDisplay)
        stopwatchDisplay.style.display = shouldRestoreManualEntry ? "none" : "block";
      if (timePickers)
        timePickers.style.display = shouldRestoreManualEntry ? "block" : "none";
      if (durationDisplay)
        durationDisplay.style.display = shouldRestoreManualEntry ? "block" : "none";

      const shouldShowForm = hasServerCloseout || shouldRestoreManualEntry;
      if (closeoutForm)
        closeoutForm.style.display = shouldShowForm ? "block" : "none";
      if (inactiveBanner)
        inactiveBanner.style.display = shouldShowForm ? "none" : "block";
    }

    const manualDateEl = document.getElementById(
      "input-manual-date",
    ) as HTMLInputElement;
    if (manualDateEl && draft.manualDate !== undefined) {
      manualDateEl.value = draft.manualDate;
    }

    const manualStartEl = document.getElementById(
      "input-manual-start",
    ) as HTMLInputElement;
    if (manualStartEl && draft.manualStart !== undefined) {
      manualStartEl.value = draft.manualStart;
    }

    const manualEndEl = document.getElementById(
      "input-manual-end",
    ) as HTMLInputElement;
    if (manualEndEl && draft.manualEnd !== undefined) {
      manualEndEl.value = draft.manualEnd;
    }

    if (draft.counters) {
      Object.entries(draft.counters).forEach(([id, val]) => {
        const input = document.getElementById(`input-counter-${id}`) as any;
        if (input) {
          input.value = val;
        }
      });
    }

    if (shouldRestoreManualEntry) {
      consoleActiveTabSignal.value = "closeout";
      validateManualTimeInputs();
    }
    syncConsoleTabState();
    syncConsoleUnsavedState();
  } catch (e) {
    console.error("Failed to parse draft details", e);
  }
}

async function handleOnlineSync() {
  const queueBefore = JSON.parse(
    localStorage.getItem("opm_offline_queue") || "[]",
  );
  if (queueBefore.length === 0) return;

  const success = await processOfflineQueue();
  if (success) {
    showToast("Offline requests synced successfully", "success");
    const ticketId = activeConsoleTicketIdSignal.peek();
    if (ticketId) {
      // Preserve draft and do a soft refresh that doesn't wipe active session state
      // but updates ticket details and history.
      await refreshConsoleUIWithDraft(ticketId);
    }
  }
}

function recStatusBadgeTone(status: string): string {
  switch (status) {
    case "requested":
      return "attention";
    case "manager_approved":
      return "success";
    case "manager_rejected":
      return "critical";
    case "admin_approved":
      return "success";
    case "admin_rejected":
      return "critical";
    case "dispatched":
      return "info";
    case "installed":
      return "success";
    default:
      return "info";
  }
}

function recStatusLabel(status: string): string {
  switch (status) {
    case "requested":
      return "Requested";
    case "manager_approved":
      return "Mgr Approved";
    case "manager_rejected":
      return "Mgr Rejected";
    case "admin_approved":
      return "Admin Approved";
    case "admin_rejected":
      return "Admin Rejected";
    case "dispatched":
      return "Dispatched";
    case "installed":
      return "Installed";
    default:
      return status;
  }
}

function getRecommendationApprovalDisplay(
  status: string,
  isRentedAsset: boolean,
): { badge: string; tone: string; subtext: string } {
  switch (status) {
    case "requested":
      return {
        badge: "Pending Manager Signature",
        tone: "attention",
        subtext: "Manager approval is required before dispatch review.",
      };
    case "manager_approved":
      return isRentedAsset
        ? {
          badge: "Manager Signed - Pending Admin Signature",
          tone: "attention",
          subtext:
            "Admin signature required for rented fleet assets before dispatch.",
        }
        : {
          badge: "Approved - Ready for Install",
          tone: "success",
          subtext: "Manager approved. Part is ready for installation.",
        };
    case "admin_approved":
      return {
        badge: "Approved - Ready for Install",
        tone: "success",
        subtext: "Required approvals are complete. Ready for installation.",
      };
    case "installed":
      return {
        badge: "Installed",
        tone: "success",
        subtext: "Part workflow completed.",
      };
    case "manager_rejected":
      return {
        badge: "Manager Rejected",
        tone: "critical",
        subtext: "Request was rejected during manager review.",
      };
    case "admin_rejected":
      return {
        badge: "Admin Rejected",
        tone: "critical",
        subtext: "Request was rejected during admin review.",
      };
    default:
      return {
        badge: recStatusLabel(status),
        tone: recStatusBadgeTone(status),
        subtext: "",
      };
  }
}

async function fetchRecommendations() {
  const ticketId = activeConsoleTicketIdSignal.peek();
  if (!ticketId) return;
  try {
    const res = await fetch(
      `/api/v1/parts-recommendations?ticketId=${ticketId}`,
      { headers: getAuthHeaders() },
    );
    if (res.ok) {
      recommendedPartsSignal.value = await res.json();
      renderRecommendationsList();
    }
  } catch (err) {
    console.error("Failed to fetch recommendations", err);
  }
}

function redirectToTicketsWithAccessError(message: string): void {
  activeConsoleTicketSignal.value = null;
  activeConsoleSessionSignal.value = null;
  recommendedPartsSignal.value = [];
  showToast(message, "error");
  window.location.hash = "#tickets";
}

function showSuccessSummaryScreen(session: any, isManual: boolean) {
  forceRebuildShell();
  const app = document.getElementById("app");
  if (!app) return;

  const durationText = formatDurationWords(
    session.startedAt,
    session.endedAt || null,
  );

  const lat = session.latitude;
  const lng = session.longitude;
  const gpsText =
    lat !== null && lat !== undefined && lng !== null && lng !== undefined
      ? `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`
      : "Not captured";

  const photosCount = session.photoUrls ? session.photoUrls.length : 0;

  // Calculate how many meters were submitted
  let metersText = "None";
  if (session.readingId) {
    metersText = "Meters recorded";
  }

  // Clear stopwatch interval and local storage stopwatch cache
  stopStopwatchLoop();
  clearLegacySessionCache();
  uploadedPhotosSignal.value = [];
  manualEntrySignal.value = false;
  consoleUnsavedWorkSignal.value = false;
  pendingConsoleNavigationRoute = null;

  app.innerHTML = `
    <s-app-provider>
      <div style="display:flex; justify-content:center; align-items:center; min-height:80vh; background-color: var(--p-color-bg-app, #f6f6f7); padding: 2rem;">
        <s-box padding="loose" background="surface" border="base" borderRadius="base" style="max-width: 500px; width: 100%; box-shadow: var(--p-shadow-100);">
          <s-stack gap="base" align="center" style="text-align:center;">
            <div style="font-size:3.5rem; margin-bottom:0.5rem;">🎉</div>
            <s-heading element="h2" style="font-weight:700;">Visit Log Submitted!</s-heading>
            <s-text tone="subdued">Your technician visit details have been successfully recorded in OPM.</s-text>

            <s-box padding="base" background="surface-secondary" borderRadius="base" style="width:100%; text-align:left; margin-top:1.5rem; border:1px solid var(--p-color-border-subdued);">
              <s-stack gap="small">
                <s-text style="font-weight:600; font-size:0.875rem; text-transform:uppercase; color:var(--p-color-text-subdued);">Visit Details Summary</s-text>
                <div style="display:grid; grid-template-columns:130px 1fr; gap:0.4rem 0.5rem; font-size:0.875rem; margin-top:0.25rem;">
                  <s-text tone="subdued">Type</s-text>
                  <s-text>${isManual ? "Retroactive Log" : "Live Stopwatch visit"}</s-text>
                  <s-text tone="subdued">Recorded Duration</s-text>
                  <s-text style="font-weight:600;">${durationText}</s-text>
                  <s-text tone="subdued">GPS Location</s-text>
                  <s-text>${gpsText}</s-text>
                  <s-text tone="subdued">Photos Attached</s-text>
                  <s-text>${photosCount} photo(s)</s-text>
                  <s-text tone="subdued">Meter Readings</s-text>
                  <s-text>${metersText}</s-text>
                </div>
              </s-stack>
            </s-box>

            <div style="margin-top:2rem; width:100%;">
              <s-button id="btn-success-close-now" variant="primary" style="width:100%; min-height:44px;">Back to Tickets List</s-button>
              <s-text tone="subdued" style="font-size:0.8rem; margin-top:0.75rem; display:block;">Auto-redirecting in <span id="success-countdown-secs" style="font-weight:600;">5</span> seconds...</s-text>
            </div>
          </s-stack>
        </s-box>
      </div>
    </s-app-provider>
  `;

  let countdown = 5;
  const countdownInterval = setInterval(() => {
    countdown--;
    const counterEl = document.getElementById("success-countdown-secs");
    if (counterEl) {
      counterEl.textContent = String(countdown);
    }
    if (countdown <= 0) {
      clearInterval(countdownInterval);
      window.location.hash = "#tickets";
    }
  }, 1000);

  const closeBtn = document.getElementById("btn-success-close-now");
  if (closeBtn) {
    closeBtn.addEventListener("click", () => {
      clearInterval(countdownInterval);
      window.location.hash = "#tickets";
    });
  }
}

export async function refreshConsoleUIWithDraft(ticketId: string) {
  saveConsoleDraft(ticketId);

  try {
    const h = getAuthHeaders();
    const resTicket = await fetch(`/api/v1/tickets/${ticketId}`, {
      headers: h,
    });
    if (resTicket.ok) {
      const t = (await resTicket.json()) as Ticket;
      activeConsoleTicketSignal.value = t;
      if (t.assetId) {
        const resReadings = await fetch(
          `/api/v1/assets/${t.assetId}/readings`,
          { headers: h },
        );
        if (resReadings.ok) {
          const readings = await resReadings.json();
          if (readings && readings.length > 0) {
            lastAssetReadingSignal.value = readings[0];
          } else {
            lastAssetReadingSignal.value = null;
          }
        } else {
          lastAssetReadingSignal.value = null;
        }
      } else {
        lastAssetReadingSignal.value = null;
      }
    }
    await refreshAssets();
    await fetchMachineHistory();
  } catch (err) {
    console.error("Failed to refresh ticket detail with draft:", err);
  }

  if (activeConsoleSessionSignal.value?.status === "awaiting_closeout") {
    // A server-finished session always uses live closeout, even if an older
    // local draft was created while manual-entry mode was selected.
    manualEntrySignal.value = false;
  }
  const app = document.getElementById("app");
  const summaryEl = document.getElementById("console-summary");
  if (app && !summaryEl) {
    app.innerHTML = renderTicketConsolePage();
    setupTicketConsolePageListeners();
  } else if (app) {
    loadConsoleDraft(ticketId);
    syncConsoleTabState();
    renderRecommendationsList();
    renderPhotoThumbnails();
    renderAssetHistoryTimeline();
  }
}

function runCountersValidation(): boolean {
  const ticket = activeConsoleTicketSignal.peek();
  if (!ticket || !ticket.assetId) return true;

  const pageTypes = pageTypesSignal.value || [];
  const resetChecked =
    (document.getElementById("checkbox-maintenance-reset") as HTMLInputElement)
      ?.checked || false;
  let isValid = true;

  pageTypes.forEach((pt) => {
    const input = document.getElementById(
      `input-counter-${pt.id}`,
    ) as HTMLInputElement;
    const errorDiv = document.getElementById(`error-counter-${pt.id}`);
    if (!input || !errorDiv) return;

    const lastVal = lastAssetReadingSignal.value?.counters?.[pt.id];
    const currentValStr = input.value;
    if (!currentValStr) {
      errorDiv.style.display = "none";
      return;
    }

    const currentVal = Number(currentValStr);
    if (
      lastVal !== undefined &&
      lastVal !== null &&
      currentVal < lastVal &&
      !resetChecked
    ) {
      errorDiv.textContent =
        "Value must be greater than or equal to the previous reading (or check Reset Counters if mainboard was replaced).";
      errorDiv.style.display = "block";
      isValid = false;
    } else {
      errorDiv.style.display = "none";
    }
  });

  return isValid;
}

function compressImage(file: File): Promise<Blob> {
  return new Promise((resolve) => {
    if (!file.type.startsWith("image/")) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;
        const maxDim = 1920;

        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(file);
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              resolve(file);
            }
          },
          "image/jpeg",
          0.8,
        );
      };
      img.onerror = () => resolve(file);
      img.src = e.target?.result as string;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
}

function uploadPhotoWithProgress(
  blob: Blob,
  fileName: string,
  onProgress: (pct: number) => void,
): Promise<any> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/v1/readings/upload-photo");

    const auth = getAuthHeaders();
    if (auth.Authorization) {
      xhr.setRequestHeader("Authorization", auth.Authorization);
    }

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        onProgress(percent);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch (err) {
          reject(new Error("Invalid response JSON"));
        }
      } else {
        try {
          const errData = JSON.parse(xhr.responseText);
          reject(
            new Error(
              errData.error || `Upload failed with status ${xhr.status}`,
            ),
          );
        } catch (err) {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      }
    };

    xhr.onerror = () => reject(new Error("Network error during upload"));

    const formData = new FormData();
    formData.append("file", blob, fileName);
    xhr.send(formData);
  });
}

// ─── Core Logic / Visits API ───────────────────────────────────────────────────

export async function refreshTicketConsole(ticketId: string) {
  const refreshSequence = ++sessionRefreshSequence;
  activeConsoleTicketIdSignal.value = ticketId;
  activeConsoleTicketSignal.value = null;
  uploadedPhotosSignal.value = [];
  recommendedPartsSignal.value = [];
  manualEntrySignal.value = false;
  consoleActiveTabSignal.value = "repair";
  clearLegacySessionCache();
  consoleUnsavedWorkSignal.value = false;
  pendingConsoleNavigationRoute = null;
  activeConsoleSessionSignal.value = null;
  currentTechnicianSessionSignal.value = null;
  consoleSessionStateSignal.value = "loading";
  consoleSessionViewSignal.value = { state: "loading", session: null, activeSession: null, elapsedSeconds: null, startedAtMs: null, message: null };
  consoleSessionLastVerifiedSignal.value = null;
  stopStopwatchLoop();
  clearLegacySessionCache();

  try {
    const h = getAuthHeaders();

    // 0. User-directory access is manager-only; technicians do not need it to operate the console.
    const role = userProfileSignal.peek()?.role;
    const canListUsers = role === "superadmin" || role === "admin" || role === "manager";
    if (canListUsers) {
      const usersRes = await fetch("/api/v1/users?limit=1000", { headers: h });
      if (usersRes.ok) {
        const users = (await usersRes.json()) as User[];
        const m = new Map<string, User>();
        users.forEach((u) => m.set(u.id, u));
        consoleUsersMapSignal.value = m;
      }
    } else {
      consoleUsersMapSignal.value = new Map();
    }

    // 1. Fetch single ticket detail
    const resTicket = await fetch(`/api/v1/tickets/${ticketId}`, {
      headers: h,
    });
    if (resTicket.status === 403 || resTicket.status === 404) {
      redirectToTicketsWithAccessError(
        "You do not have access to that ticket.",
      );
      return;
    }
    if (resTicket.ok) {
      const t = (await resTicket.json()) as Ticket;
      activeConsoleTicketSignal.value = t;

      if (t.assetId) {
        const resReadings = await fetch(
          `/api/v1/assets/${t.assetId}/readings`,
          { headers: h },
        );
        if (resReadings.ok) {
          const readings = await resReadings.json();
          if (readings && readings.length > 0) {
            lastAssetReadingSignal.value = readings[0];
          } else {
            lastAssetReadingSignal.value = null;
          }
        } else {
          lastAssetReadingSignal.value = null;
        }
      } else {
        lastAssetReadingSignal.value = null;
      }
    }

    // 2. Refresh other signals in background
    await refreshAssets();
    await refreshConsumables();

    // 3. Fetch ticket history and server-authoritative current technician state.
    const resSess = await fetch(`/api/v1/tickets/${ticketId}/sessions`, {
      headers: h,
    });
    if (resSess.ok) {
      const sessions = (await resSess.json()) as ServiceSession[];
      consoleSessionsSignal.value = sessions;
    }
    const currentRes = await fetch("/api/v1/service-sessions/current", { headers: h });
    if (refreshSequence !== sessionRefreshSequence || activeConsoleTicketIdSignal.peek() !== ticketId) return;
    if (!currentRes.ok) {
      setConsoleSessionView({ state: "unverified", session: null, activeSession: null, elapsedSeconds: null, startedAtMs: null, message: "Session status could not be verified. Retry sync before starting or closing a visit." });
    } else {
      const current: unknown = await currentRes.json();
      const view = deriveConsoleSessionView(ticketId, current);
      setConsoleSessionView(view);
      const rawServerTime = isRecord(current) ? current.serverTime : null;
      const serverTimeMs = parseSessionTimestamp(rawServerTime);
      consoleSessionLastVerifiedSignal.value = typeof rawServerTime === "string" && serverTimeMs !== null ? rawServerTime : null;
      serverClockOffsetMs = serverTimeMs === null ? 0 : serverTimeMs - Date.now();
      if (view.state === "active" && view.startedAtMs !== null) {
        startStopwatchLoop(view.startedAtMs);
      } else {
        stopStopwatchLoop(false);
      }
    }

    // 4. Fetch parts recommendations for this ticket
    const resRecs = await fetch(
      `/api/v1/parts-recommendations?ticketId=${ticketId}`,
      { headers: h },
    );
    if (resRecs.ok) {
      recommendedPartsSignal.value = await resRecs.json();
      renderRecommendationsList();
    } else {
      recommendedPartsSignal.value = [];
      renderRecommendationsList();
    }

    await fetchMachineHistory();
  } catch (e) {
    console.error("Failed to refresh ticket console", e);
    clearLegacySessionCache();
    if (refreshSequence === sessionRefreshSequence) {
      setConsoleSessionView({ state: "unverified", session: null, activeSession: null, elapsedSeconds: null, startedAtMs: null, message: "The console could not refresh its session status. Retry sync before changing service time." });
    }
  } finally {
    if (
      refreshSequence === sessionRefreshSequence &&
      activeConsoleTicketIdSignal.peek() === ticketId
    ) {
      // Imperatively re-render: the initial render produced a spinner because
      // activeConsoleTicketSignal was null.  Now that data is loaded, swap the
      // content slot so the full console appears.
      const slot = document.getElementById("main-content-slot");
      if (slot) {
        slot.innerHTML = renderTicketConsolePage();
      }
      setupTicketConsolePageListeners();
    }
  }
}

async function retryTicketConsoleSessionSync(ticketId: string): Promise<void> {
  saveConsoleDraft(ticketId);
  consoleSessionStateSignal.value = "submitting";
  try {
    const response = await fetch("/api/v1/service-sessions/current", { headers: getAuthHeaders() });
    if (!response.ok) throw new Error("Session sync could not be completed.");
    const context: unknown = await response.json();
    if (activeConsoleTicketIdSignal.peek() !== ticketId) return;
    const view = deriveConsoleSessionView(ticketId, context);
    setConsoleSessionView(view);
    const rawServerTime = isRecord(context) ? context.serverTime : null;
    const serverTimeMs = parseSessionTimestamp(rawServerTime);
    serverClockOffsetMs = serverTimeMs === null ? 0 : serverTimeMs - Date.now();
    consoleSessionLastVerifiedSignal.value = typeof rawServerTime === "string" && serverTimeMs !== null ? rawServerTime : null;
    if (view.state === "active" && view.startedAtMs !== null) startStopwatchLoop(view.startedAtMs);
    else stopStopwatchLoop(false);
  } catch (error) {
    setConsoleSessionView({ state: "unverified", session: null, activeSession: null, elapsedSeconds: null, startedAtMs: null, message: error instanceof Error ? error.message : "Session sync could not be completed." });
  }
  const app = document.getElementById("app");
  if (app && activeConsoleTicketIdSignal.peek() === ticketId) {
    app.innerHTML = renderTicketConsolePage();
    setupTicketConsolePageListeners();
  }
}

// ─── Page Renderer ─────────────────────────────────────────────────────────────

export function renderTicketConsolePage(): string {
  const ticket = activeConsoleTicketSignal.value;
  const userRole = userProfileSignal.peek()?.role;
  const isAdmin = userRole === "superadmin" || userRole === "admin";
  if (!ticket) {
    return `
      <s-page heading="Technician Console">
        <s-stack gap="base" alignment="center" style="padding:5rem 0;">
          <s-spinner size="large"></s-spinner>
          <s-text tone="subdued">Loading technician console cockpit…</s-text>
        </s-stack>
      </s-page>
    `;
  }

  const customers = allCustomersSignal.peek() || [];
  const sites = sitesSignal.peek() || [];
  const assets = assetsListSignal.peek() || [];
  const pageTypes = pageTypesSignal.peek() || [];
  const asset = assets.find((a) => a.id === ticket.assetId);
  const assetHasGps = !!(
    asset &&
    asset.latitude !== null &&
    asset.latitude !== undefined &&
    asset.longitude !== null &&
    asset.longitude !== undefined
  );

  const serialNumberRaw = (ticket.serialNumberRaw || "").trim();
  const assetSerial = (asset?.serialNumber || "").trim();
  const isSerialPendingOrEmpty = ticket.assetId
    ? (assetSerial.startsWith("PENDING-SERIAL-NUMBER") || assetSerial === "")
    : (serialNumberRaw.startsWith("PENDING-SERIAL-NUMBER") || serialNumberRaw === "");

  const custName = escapeHtml(
    customers.find((c) => c.id === ticket.customerId)?.name ||
    "Unknown Customer",
  );

  let siteName = "—";
  let siteAddress = "—";
  let mapsLinkHtml = "";
  const ticketSite = ticket.siteId
    ? sites.find((item) => item.id === ticket.siteId)
    : null;
  if (ticket.siteId) {
    if (ticketSite) {
      siteName = escapeHtml(ticketSite.name);
      if (ticketSite.address) {
        siteAddress = escapeHtml(ticketSite.address);
        const encodedAddr = encodeURIComponent(ticketSite.address);
        mapsLinkHtml = `
          <s-button id="console-maps-link" href="https://www.google.com/maps/search/?api=1&query=${encodedAddr}" target="_blank" size="slim" variant="plain">
            <s-icon type="location" size="small"></s-icon> Navigate with Google Maps
          </s-button>
        `;
      }
    }
  }

  // Linked Asset details
  let linkedAssetHtml = "";
  if (ticket.assetId) {
    if (asset) {
      const model = (deviceModelsSignal.peek() || []).find(
        (m) => m.id === asset.modelId,
      );
      const cleanModel = escapeHtml(
        model ? `${model.vendor} ${model.modelName}` : "—",
      );
      const cleanSerial = escapeHtml(asset.serialNumber || "—");
      const cleanOwnership = escapeHtml(asset.ownershipType || "—");
      linkedAssetHtml = `
        <s-box padding="base" background="surface-secondary" borderRadius="base">
          <div style="display:grid;grid-template-columns:120px 1fr;gap:0.35rem 0.5rem;font-size:0.875rem;">
            <s-text tone="subdued" style="font-weight:600;">Model Name</s-text>
            <s-text>${cleanModel}</s-text>
            <s-text tone="subdued" style="font-weight:600;">Serial Number</s-text>
            <s-link href="#machine-history?id=${encodeURIComponent(ticket.assetId)}">${cleanSerial}</s-link>
            <s-text tone="subdued" style="font-weight:600;">Ownership Type</s-text>
            <s-text style="text-transform:capitalize;">${cleanOwnership}</s-text>
          </div>
        </s-box>
      `;
    } else {
      linkedAssetHtml = `
        <s-banner tone="attention">
          Linked asset details are unavailable.
        </s-banner>
      `;
    }
  } else {
    linkedAssetHtml = `
      <s-box padding="base" background="surface-secondary" borderRadius="base" style="text-align:center;">
        <s-stack gap="base" align="center">
          <s-text tone="subdued" style="font-style:italic;">No printer asset currently linked to this ticket.</s-text>
          <s-button id="btn-open-link-asset" variant="primary" size="slim">Link Printer Asset</s-button>
        </s-stack>
      </s-box>
    `;
  }

  // Meters inputs section
  let metersInputsHtml = "";
  if (ticket.assetId) {
    metersInputsHtml = `
      <s-form id="form-console-meters">
        <s-stack gap="base">
          <s-text>Input current cumulative meter counters observed on the device.</s-text>
          ${pageTypes
        .map((pt) => {
          const lastVal = lastAssetReadingSignal.value?.counters?.[pt.id];
          const lastValStr =
            lastVal !== undefined && lastVal !== null
              ? String(lastVal)
              : "None";
          return `
              <div>
                <s-number-field
                  label="Cumulative Count: ${pt.displayName} (Last Reading: ${lastValStr})"
                  name="counter_${pt.id}"
                  id="input-counter-${pt.id}"
                  placeholder="e.g. 15000"
                  type="number"
                  inputmode="numeric"
                  required>
                </s-number-field>
                <div id="error-counter-${pt.id}" style="display:none; color:var(--p-color-text-critical); font-size:0.8rem; margin-top:0.25rem;"></div>
              </div>
            `;
        })
        .join("")}
          <s-checkbox
            id="checkbox-maintenance-reset"
            label="Reset Counters (Mainboard Change / Rollover)"
            name="isMaintenanceReset">
          </s-checkbox>
        </s-stack>
      </s-form>
    `;
  } else {
    metersInputsHtml = `
      <s-banner tone="attention">
        Please link a printer asset first to record meter readings.
      </s-banner>
    `;
  }

  const sessionView = consoleSessionViewSignal.value;
  const isSessionActive = sessionView.state === "active";
  const isCloseoutReady = sessionView.state === "awaiting_closeout";
  const isSessionRecovering = sessionView.state === "incomplete_session" || sessionView.state === "unverified";
  const hasSession = isSessionActive || isCloseoutReady;
  const activeTab = consoleActiveTabSignal.value;
  const isCloseoutDisabled = !isCloseoutReady && !manualEntrySignal.value;
  const stopwatchDisplayValue = sessionView.elapsedSeconds === null ? "Time unavailable" : formatDuration(sessionView.elapsedSeconds);
  const stopwatchButtonLabel = isCloseoutReady
    ? "Service Time Finished"
    : !isSessionActive
    ? "Start Service Stopwatch"
    : "Finish Service";
  const stopwatchButtonVariant = isCloseoutReady
    ? "plain"
    : !isSessionActive
    ? "primary"
    : "critical";

  const cachedActiveTicketId = sessionView.activeSession?.ticketId;
  let crossTicketBannerHtml = "";
  if (cachedActiveTicketId && cachedActiveTicketId !== ticket.id) {
    crossTicketBannerHtml = `
      <s-banner tone="warning" title="Active visit on another ticket">
        <s-stack gap="small">
          <s-text>You have a visit in progress on ticket ${escapeHtml(cachedActiveTicketId || "another ticket")}. Finish it there before starting another timed visit.</s-text>
          <div>
            <s-button id="btn-switch-to-active-ticket" variant="primary" data-ticket-id="${escapeHtml(cachedActiveTicketId)}">
              Switch to Active Ticket Console
            </s-button>
          </div>
        </s-stack>
      </s-banner>
    `;
  }

  return `
    <s-page heading="Technician Console Room" inlineSize="large">
      ${crossTicketBannerHtml}
      <s-section>
        <s-stack gap="large">
          <s-box padding="base" background="surface" border="base" borderRadius="base">
            <s-grid gridTemplateColumns="repeat(auto-fit, minmax(280px, 1fr))" gap="base" alignItems="start">
              <s-stack gap="small">
                <s-stack direction="inline" gap="small" align="center">
                  <s-heading element="h2" style="font-weight:700;margin:0;">${ticket.ticketNumber}</s-heading>
                  <s-badge tone="${ticket.priority === "critical" ? "critical" : ticket.priority === "high" ? "warning" : "info"}">
                    ${ticket.priority.toUpperCase()}
                  </s-badge>
                </s-stack>
                <s-text tone="subdued" style="font-weight:600;font-size:0.9rem;">Customer: ${custName}</s-text>
                <s-text style="font-size:0.875rem;">Site: <strong>${siteName}</strong> · Address: <span tone="subdued">${siteAddress}</span></s-text>
                <s-stack direction="inline" gap="small">
                  <s-button variant="plain" size="slim" id="console-back-btn">
                    <s-icon type="arrow-left" size="small"></s-icon> Back to Tickets
                  </s-button>
                  <s-button variant="plain" size="slim" id="btn-suggest-ticket" style="min-height:44px;">Found another machine with an issue?</s-button>
                  ${mapsLinkHtml}
                </s-stack>
              </s-stack>

              <s-box padding="base" background="surface-secondary" borderRadius="base">
                <s-stack gap="base" align="center">
                  <s-text style="font-weight:600;font-size:0.8rem;text-transform:uppercase;color:var(--p-color-text-subdued);">Visit state</s-text>
                  <s-heading element="h2">${isSessionActive ? "Visit in progress" : isCloseoutReady ? "Ready for closeout" : isSessionRecovering ? "Session needs attention" : "No visit in progress"}</s-heading>
                  ${isSessionActive ? "<s-badge tone=\"success\">Resumed from server</s-badge>" : isCloseoutReady ? "<s-badge tone=\"success\">Service time recorded</s-badge>" : ""}
                  ${isSessionRecovering && sessionView.message ? `<s-banner tone="critical" title="Session recovery required"><s-stack gap="small"><s-paragraph>${escapeHtml(sessionView.message)}</s-paragraph><s-stack direction="inline" gap="small"><s-button id="btn-retry-session-sync" variant="primary">Retry session sync</s-button><s-button id="btn-return-to-tickets">Back to tickets</s-button></s-stack></s-stack></s-banner>` : ""}
                  <s-text tone="subdued">${isSessionActive ? "Keep working in Repair & Parts. Finish service time when physical work is complete, then complete closeout." : isCloseoutReady ? "Service time is fixed. Complete the Closeout Summary when ready." : "Start a timed visit when you arrive, or log a completed historical visit manually."}</s-text>
                  <s-text id="console-stopwatch-display" style="font-family:monospace;font-size:2.25rem;font-weight:700;">${manualEntrySignal.value ? "Manual visit" : stopwatchDisplayValue}</s-text>
                  <s-button id="btn-toggle-stopwatch" variant="${stopwatchButtonVariant}" ${isCloseoutReady || isSessionRecovering || sessionView.state === "active_elsewhere" ? "disabled" : ""} style="min-height:44px;touch-action:manipulation;width:100%;">
                    ${stopwatchButtonLabel}
                  </s-button>

                  <div id="manual-hours-container" style="display:${hasSession || isSessionRecovering ? "none" : "block"}; width:100%; text-align:left;">
                    <s-checkbox
                      id="checkbox-log-manual"
                      label="Log Retroactive Service Visit"
                      name="manualEntry"
                      ${manualEntrySignal.value ? "checked" : ""}>
                    </s-checkbox>
                    <div id="manual-time-pickers" style="display:${manualEntrySignal.value ? "block" : "none"};margin-top:1rem;">
                      <s-stack gap="small">
                        <div>
                          <label for="input-manual-date" style="display:block;font-size:0.875rem;font-weight:500;margin-bottom:0.25rem;">Date of Visit</label>
                          <input type="date" id="input-manual-date" name="manualDate" style="padding:0.5rem;border:1px solid #c4cdd5;border-radius:4px;width:100%;box-sizing:border-box;background:white;min-height:44px;" />
                        </div>
                        <div style="display:grid; grid-template-columns:1fr 1fr; gap:1rem;">
                          <div>
                            <label for="input-manual-start" style="display:block;font-size:0.875rem;font-weight:500;margin-bottom:0.25rem;">Start Time</label>
                            <input type="time" id="input-manual-start" name="manualStart" style="padding:0.5rem;border:1px solid #c4cdd5;border-radius:4px;width:100%;box-sizing:border-box;background:white;min-height:44px;" />
                          </div>
                          <div>
                            <label for="input-manual-end" style="display:block;font-size:0.875rem;font-weight:500;margin-bottom:0.25rem;">End Time</label>
                            <input type="time" id="input-manual-end" name="manualEnd" style="padding:0.5rem;border:1px solid #c4cdd5;border-radius:4px;width:100%;box-sizing:border-box;background:white;min-height:44px;" />
                          </div>
                        </div>
                      </s-stack>
                    </div>
                  </div>
                </s-stack>
              </s-box>
            </s-grid>
          </s-box>

          <s-stack gap="base">
            <div class="opm-console-tabs" role="tablist" aria-label="Ticket console workflow" style="display:flex;gap:0.5rem;border-bottom:1px solid #e1e3e5;padding-bottom:0.5rem;flex-wrap:wrap;">
              <s-button id="tab-btn-repair" role="tab" aria-controls="tab-content-repair" aria-selected="${activeTab === "repair"}" variant="${activeTab === "repair" ? "primary" : "plain"}">
                1. Repair & Parts
              </s-button>
              <s-button id="tab-btn-closeout" role="tab" aria-controls="tab-content-closeout" aria-selected="${activeTab === "closeout"}" variant="${activeTab === "closeout" ? "primary" : "plain"}" ${isCloseoutDisabled ? "disabled" : ""}>
                2. Closeout Summary
              </s-button>
            </div>

            <div id="tab-content-repair" role="tabpanel" aria-labelledby="tab-btn-repair" style="display:${activeTab === "repair" ? "block" : "none"};">
              <s-stack gap="base">
                ${isSerialPendingOrEmpty
      ? `
                <div id="console-serial-missing-banner-container" style="margin-bottom:0.5rem;">
                  <s-banner tone="critical" title="Printer Serial Number Missing or Pending">
                    <s-stack gap="small">
                      <s-paragraph>The serial number for this printer is missing or registered as pending. Update the serial number in the Asset details below before resolving this ticket.</s-paragraph>
                    </s-stack>
                  </s-banner>
                </div>
                `
      : ""
    }
                ${ticket.assetId && !assetHasGps
      ? `
                <div id="console-gps-missing-banner-container">
                  <s-banner tone="warning" title="Asset Location Unknown">
                    <s-stack gap="small">
                      <s-paragraph>Physical GPS coordinates for this printer are unregistered. Capture location now to update records.</s-paragraph>
                      <s-stack direction="inline" gap="small" alignment="center">
                        <s-button size="slim" id="btn-gps-capture-banner">Capture GPS</s-button>
                        <s-text id="gps-banner-status" tone="subdued" style="font-size:0.8rem;"></s-text>
                      </s-stack>
                    </s-stack>
                  </s-banner>
                </div>
                `
      : ""
    }
                <s-box padding="base" background="surface" border="base" borderRadius="base">
                  <s-stack gap="base">
                    <s-heading element="h3" style="font-size:0.875rem;font-weight:600;text-transform:uppercase;color:var(--p-color-text-subdued);margin-bottom:0.25rem;">1. Context & Diagnostics</s-heading>

              <s-stack gap="small">
                <s-stack direction="inline" gap="small" align="center" style="justify-content:space-between;width:100%;">
                  <s-text style="font-weight:600;font-size:0.8rem;color:var(--p-color-text-subdued);">Failure Description</s-text>
                  <s-button id="btn-open-edit-desc-modal" variant="plain" size="slim">Edit Description</s-button>
                </s-stack>
                <s-box padding="base" background="surface-secondary" borderRadius="base">
                  <s-paragraph>${escapeHtml(ticket.description || "No failure description supplied.")}</s-paragraph>
                </s-box>
              </s-stack>

              <s-stack gap="small">
                <s-text style="font-weight:600;font-size:0.8rem;color:var(--p-color-text-subdued);">Linked Printer Asset</s-text>
                <div id="console-linked-asset-container">
                  ${linkedAssetHtml}
                </div>
              </s-stack>

              <s-stack gap="small">
                <s-text style="font-weight:600;font-size:0.8rem;color:var(--p-color-text-subdued);">Printer Details Actions</s-text>
                <s-box padding="base" background="surface-secondary" borderRadius="base">
                  <s-stack direction="inline" gap="small">
                    <s-button id="btn-open-edit-asset-modal" variant="plain" size="slim">Edit Asset Details</s-button>
                  </s-stack>
                </s-box>
              </s-stack>

              <s-stack gap="small">
                <s-text style="font-weight:600;font-size:0.8rem;color:var(--p-color-text-subdued);">Customer & Site Address</s-text>
                <s-box padding="base" background="surface-secondary" borderRadius="base">
                  <s-stack gap="small">
                    <s-text>Customer: <strong>${custName}</strong></s-text>
                    <s-text>Site: <strong>${siteName}</strong></s-text>
                    <s-text>Address: <span>${siteAddress}</span></s-text>
                    ${ticket.siteId ? `<s-button id="btn-open-edit-site-modal" variant="plain" size="slim">Edit Site Details</s-button>` : ""}
                  </s-stack>
                </s-box>
              </s-stack>
                  </s-stack>
                </s-box>

                <s-box padding="base" background="surface" border="base" borderRadius="base">
                  <s-stack gap="base">
                    <s-heading element="h3" style="font-size:0.875rem;font-weight:600;text-transform:uppercase;color:var(--p-color-text-subdued);margin-bottom:0.25rem;">2. Parts & Materials Hub</s-heading>

              <s-box padding="base" background="surface-secondary" borderRadius="base">
                <s-stack gap="base">
                  <s-text-field
                    id="console-recommend-colloquial-name"
                    label="Colloquial Part Description / Name"
                    name="colloquialName"
                    placeholder="e.g. Lower fuser roller / pick-up feed belt"
                    required
                  ></s-text-field>

                  <s-number-field label="Quantity" id="console-recommend-qty" name="qty" value="1" min="1"></s-number-field>
                  <div id="console-part-price-context" style="font-size:0.875rem; color:var(--p-color-text-subdued); font-weight:500; min-height:1.2rem;"></div>

                   <!-- Session Scope Selector -->
                  <div style="display:${isSessionActive ? "none" : "block"};">
                    <label style="display:block;font-size:0.875rem;font-weight:500;margin-bottom:0.25rem;color:var(--p-color-text, #202223);">Attach request to:</label>
                    <select id="console-session-scope" style="padding:0.5rem;border:1px solid #c4cdd5;border-radius:4px;width:100%;max-width:100%;box-sizing:border-box;background:white;color:var(--p-color-text, #202223);">
                      ${isSessionActive ? `<option value="active" selected>Current active visit session</option>` : ""}
                      <option value="direct" ${!isSessionActive ? "selected" : ""}>Direct request (no visit log/time tracking)</option>
                      ${consoleSessionsSignal.value
      .filter((s: any) => s.endedAt)
      .map(
        (s: any) => `
                        <option value="${s.id}">Visit on ${formatDateTime(s.startedAt)} (duration: ${formatDurationWords(s.startedAt, s.endedAt || null)})</option>
                      `,
      )
      .join("")}
                    </select>
                  </div>

                  <s-button id="btn-add-recommendation" variant="primary" style="min-height: 44px; touch-action: manipulation; display: inline-flex; align-items: center; justify-content: center; width: 100%;">Add Recommendation</s-button>
                </s-stack>
              </s-box>

              <s-stack gap="small">
                <s-text style="font-weight:600;font-size:0.8rem;color:var(--p-color-text-subdued);">Current Part Requests</s-text>
                <div id="console-recommendations-list" style="max-height: 350px; overflow-y: auto; padding-right: 0.5rem; padding-bottom: 2rem; box-sizing: border-box;">
                  <!-- Rendered dynamically -->
                  <s-text tone="subdued" style="font-style:italic;">No parts recommended yet.</s-text>
                </div>
              </s-stack>
                  </s-stack>
                </s-box>

                <s-box padding="base" background="surface" border="base" borderRadius="base">
                  <s-stack gap="base">
                    <s-stack gap="extraSmall">
                      <s-heading element="h3" style="font-size:0.875rem;font-weight:600;text-transform:uppercase;color:var(--p-color-text-subdued);margin-bottom:0.25rem;">3. Machine Service Timeline</s-heading>
                      <s-text tone="subdued">Chronological history for the linked printer asset, or for this ticket if the asset is not yet linked.</s-text>
                    </s-stack>
                    <s-button id="btn-console-history-load-more" variant="plain" size="slim" style="align-self:flex-start; display:none;">Load older history</s-button>
                    <div id="console-asset-history-empty" style="display:none;"></div>
                    <div id="console-asset-history-list" style="display:flex;flex-direction:column;gap:0.75rem;"></div>
                  </s-stack>
                </s-box>
              </s-stack>
            </div>

            <div id="tab-content-closeout" role="tabpanel" aria-labelledby="tab-btn-closeout" style="display:${activeTab === "closeout" ? "block" : "none"};">
              <s-box padding="base" background="surface" border="base" borderRadius="base">
                <s-stack gap="base">
                  <s-heading element="h3" style="font-size:0.875rem;font-weight:600;text-transform:uppercase;color:var(--p-color-text-subdued);margin-bottom:0.25rem;">Closeout Summary</s-heading>

                  <div id="visit-inactive-banner" style="display:${hasSession || manualEntrySignal.value ? "none" : "block"};">
                    <s-banner tone="info">
                      Start a service stopwatch session or check "Log Hours Manually" to log a technician visit.
                    </s-banner>
                  </div>

                  <div id="visit-closeout-form" style="display:${hasSession || manualEntrySignal.value ? "block" : "none"};">
                    <s-stack gap="base">
                      <div id="console-meters-form-container" style="margin-bottom: 0.5rem;">
                        ${metersInputsHtml}
                      </div>

                      <s-stack gap="small" style="margin-bottom: 0.5rem;">
                        <s-text style="font-weight:600;font-size:0.8rem;text-transform:uppercase;color:var(--p-color-text-subdued);">Photo Evidence (Logs/Receipts)</s-text>
                        <s-box padding="base" background="surface" border="base" borderRadius="base">
                          <s-stack gap="base">
                            <input type="file" id="console-photo-file" accept="image/*" capture="camera" style="display:none;" />
                            <s-button id="btn-trigger-upload">Upload Photo Evidence</s-button>
                            <div id="console-photo-thumbnails" style="display:flex;gap:0.5rem;flex-wrap:wrap;margin-top:0.5rem;">
                              <!-- Rendered dynamically -->
                            </div>
                          </s-stack>
                        </s-box>
                      </s-stack>

                      ${!assetHasGps && ticket.assetId
      ? `
                      <s-box padding="base" background="surface-secondary" borderRadius="base" style="border:1px dashed #c4cdd5; margin-bottom: 0.5rem;">
                        <s-stack gap="small">
                          <s-text style="font-weight:600;font-size:0.8rem;text-transform:uppercase;color:var(--p-color-text-subdued);">GPS Location Capture</s-text>
                          <s-checkbox
                            id="checkbox-capture-gps"
                            label="Record printer GPS location to update asset records"
                            checked>
                          </s-checkbox>
                          <div id="console-gps-status" style="font-size:0.85rem; margin-top: 0.25rem;">
                            <s-text tone="subdued" style="font-style:italic;">Location will be captured on submit if checked.</s-text>
                          </div>
                        </s-stack>
                      </s-box>
                      `
      : ""
    }

                      <div id="manual-duration-display" style="display:none;">
                        <s-box padding="base" background="surface-secondary" borderRadius="base">
                          <s-stack gap="small">
                            <s-text style="font-weight:600;font-size:0.8rem;text-transform:uppercase;color:var(--p-color-text-subdued);">Calculated Duration</s-text>
                            <s-text id="manual-duration-value" style="font-size:1.1rem;font-weight:600;">—</s-text>
                            <div id="manual-duration-error" style="display:none;color:var(--p-color-text-critical);font-size:0.8rem;"></div>
                          </s-stack>
                        </s-box>
                      </div>

                      <s-text-area
                        id="console-summary"
                        label="Technician Visit Notes"
                        name="summary"
                        placeholder="Describe work completed, tests run, problems fixed..."
                        rows="4"
                        required
                      ></s-text-area>

                      <s-checkbox
                        id="checkbox-is-final"
                        label="Did this session fix the failure? (Resolves ticket)"
                        name="isFinal"
                      ></s-checkbox>

                      <div id="console-banner-container" style="display:none;margin-bottom:0.5rem;"></div>
                      <div id="console-error-banner" style="display:none;color:var(--p-color-text-critical);font-size:0.875rem;padding:0.5rem 0;"></div>
                      <div id="console-submit-requirements" style="display:none;margin-bottom:0.5rem;"></div>
                      <s-button variant="primary" id="btn-submit-stop-session" style="min-height: 44px; touch-action: manipulation; display: inline-flex; align-items: center; justify-content: center; width: 100%;">Submit & Close Visit Session</s-button>
                    </s-stack>
                  </div>
                </s-stack>
              </s-box>
            </div>
          </s-stack>
        </s-stack>
      </s-section>
    </s-page>

    <s-modal id="console-link-asset-modal" heading="Link Printer Asset">
      <s-stack gap="base">
        <s-text tone="subdued">Select a printer asset to link to this ticket.</s-text>
        <s-box padding="base" background="surface-secondary" borderRadius="base" style="border: 1px solid var(--p-color-border-subdued); margin: 0.25rem 0;">
          <s-text tone="subdued" style="font-size:0.8rem; font-style:italic;">
            Only showing fleet printers registered for <b>${custName}</b>. If the printer isn't listed, please verify or correct the ticket's customer profile.
          </s-text>
        </s-box>
        <opm-autocomplete id="select-link-asset" label="Registered asset" placeholder="Search by serial number or model"></opm-autocomplete>
        <div id="link-asset-error" style="display:none;color:var(--p-color-text-critical);font-size:0.875rem;"></div>
        <s-stack direction="inline" gap="small" style="margin-top:0.5rem;justify-content:space-between;width:100%;">
          <s-stack direction="inline" gap="small">
            <s-button variant="primary" id="btn-save-linked-asset">Link Asset</s-button>
            <s-button commandFor="console-link-asset-modal" command="--hide">Cancel</s-button>
          </s-stack>
          <s-button id="btn-open-console-register-asset">Register New Asset</s-button>
        </s-stack>
      </s-stack>
    </s-modal>

    <!-- Register Asset Modal -->
    <s-modal id="console-register-asset-modal" heading="Register New Fleet Asset">
      <s-form id="form-console-register-asset">
        <s-stack gap="base">
          <s-text-field label="Serial Number" name="serialNumber" placeholder="e.g. SN-HP-9876" required></s-text-field>

          <div style="display:flex; flex-direction:column; gap:0.25rem;">
            <label for="console-reg-site-select" style="font-size:0.875rem; font-weight:500;">Assign Site</label>
            <select name="siteId" id="console-reg-site-select" style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface); min-height:44px;">
              ${sites
      .filter((s) => s.customerId === ticket.customerId)
      .map((s) => `<option value="${s.id}">${escapeHtml(s.name)}</option>`)
      .join("")}
            </select>
            <s-checkbox
              id="checkbox-reg-create-site-inline"
              label="Site not listed? Add new site details inline."
              name="createSiteInline">
            </s-checkbox>
            <div id="console-reg-inline-site-fields" style="display:none; padding:0.75rem; border:1px dashed var(--p-color-border-subdued); border-radius:4px; margin-top:0.5rem; background:var(--p-color-bg-surface-secondary);">
              <s-stack gap="base">
                <s-text-field label="New Site Name" name="newSiteName" id="input-reg-site-name" placeholder="e.g. 4th Floor IT Desk"></s-text-field>
                <s-stack direction="inline" gap="small" alignment="center">
                  <s-number-field label="Latitude" name="newSiteLatitude" id="input-reg-site-lat" step="0.00001" style="flex:1;"></s-number-field>
                  <s-number-field label="Longitude" name="newSiteLongitude" id="input-reg-site-lng" step="0.00001" style="flex:1;"></s-number-field>
                </s-stack>
                <s-stack direction="inline" gap="small" alignment="center">
                  <s-button size="slim" id="btn-reg-site-capture-gps">Use Current GPS</s-button>
                  <s-text id="reg-site-gps-status" tone="subdued" style="font-size:0.8rem;"></s-text>
                </s-stack>
              </s-stack>
            </div>
          </div>

          <div style="display:flex; flex-direction:column; gap:0.25rem;">
            <label style="font-size:0.875rem; font-weight:500;">Device Model</label>
            <select name="modelId" style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface);">
              ${(deviceModelsSignal.value || []).map((m) => `<option value="${m.id}">${escapeHtml(`${m.vendor} ${m.modelName}`.trim())}</option>`).join("")}
            </select>
          </div>

          <div style="display:flex; flex-direction:column; gap:0.25rem;">
            <label style="font-size:0.875rem; font-weight:500;">Ownership Structure</label>
            <select name="ownershipType" style="padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface);">
              <option value="owned">Owned</option>
              <option value="rented" selected>Rented (MPS Contract)</option>
            </select>
          </div>

          <div style="display:flex; align-items:center; gap:0.5rem; margin-top:0.25rem;">
            <input type="checkbox" id="console-reg-is-mps" name="isMps" checked style="cursor:pointer;" />
            <label for="console-reg-is-mps" style="font-size:0.875rem; font-weight:500; cursor:pointer;">MPS Managed Asset</label>
          </div>

          ${isAdmin
      ? `
            <details style="margin-top:0.5rem; cursor:pointer; width:100%;">
              <summary style="font-size:0.875rem; font-weight:500; color:var(--p-color-text-subdued);">Advanced Polling Settings</summary>
              <div style="margin-top:0.75rem;">
                <s-stack gap="base">
                  <s-number-field label="Polling Interval (seconds)" name="intervalSeconds" value="3600" required></s-number-field>
                  <s-number-field label="Anomaly Sensitivity Multiplier" name="anomalySensitivityMultiplier" value="3.0" required></s-number-field>
                </s-stack>
              </div>
            </details>
          `
      : `
            <input type="hidden" name="intervalSeconds" value="3600" />
            <input type="hidden" name="anomalySensitivityMultiplier" value="3.0" />
          `
    }

          <div id="console-register-asset-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem;"></div>

          <s-stack direction="inline" gap="small" style="margin-top:1rem;">
            <s-button variant="primary" id="btn-console-submit-register-asset">Register</s-button>
            <s-button id="btn-console-cancel-register-asset" commandFor="console-register-asset-modal" command="--hide">Cancel</s-button>
          </s-stack>
        </s-stack>
      </s-form>
    </s-modal>

    <s-modal id="confirm-cancel-recommendation-modal" heading="Cancel Recommendation Request">
      <s-stack gap="base">
        <s-banner tone="warning" title="Confirm cancellation">
          <s-paragraph>Canceling a recommendation request will remove it from the current workflow. This action cannot be undone.</s-paragraph>
        </s-banner>
        <s-text id="confirm-cancel-recommendation-text" tone="subdued"></s-text>
        <s-stack direction="inline" gap="small" style="justify-content:flex-end;">
          <s-button id="btn-confirm-cancel-recommendation" variant="critical">Cancel Request</s-button>
          <s-button commandFor="confirm-cancel-recommendation-modal" command="--hide">Keep Request</s-button>
        </s-stack>
      </s-stack>
    </s-modal>

    <s-modal id="console-edit-asset-modal" heading="Edit Printer Details">
      <s-stack gap="base">
        ${ticket.assetId
      ? `
          <div>
            <opm-autocomplete id="console-override-model" label="Device model" placeholder="Search model catalog"></opm-autocomplete>
          </div>
          <s-text-field
            id="console-override-serial"
            label="Serial Number"
            name="overrideSerial"
            value="${escapeHtml(asset ? asset.serialNumber || "" : "")}"
            placeholder="Enter serial number"
          ></s-text-field>
        `
      : `
          <opm-autocomplete
            id="console-override-model-raw"
            label="Model Name"
            placeholder="Search catalog or type a new model"
            allow-custom="true"
          ></opm-autocomplete>
          <s-text-field
            id="console-override-serial-raw"
            label="Serial Number"
            name="overrideSerialRaw"
            value="${escapeHtml(ticket.serialNumberRaw || "")}"
            placeholder="Enter serial number"
          ></s-text-field>
        `
    }
        <div id="console-override-feedback" style="display:none;font-size:0.8rem;padding:0.25rem 0;"></div>
        <s-stack direction="inline" gap="small" style="justify-content:flex-end;">
          <s-button id="btn-save-printer-details" variant="primary">Review Save</s-button>
          <s-button commandFor="console-edit-asset-modal" command="--hide">Cancel</s-button>
        </s-stack>
      </s-stack>
    </s-modal>

    <s-modal id="console-edit-site-modal" heading="Edit Site Details">
      <s-stack gap="base">
        <s-text-field
          id="console-edit-site-name"
          label="Site Name"
          name="siteName"
          value="${escapeHtml(ticketSite?.name || "")}"
          placeholder="Enter site name"
          required
        ></s-text-field>
        <s-stack direction="inline" gap="small" alignment="center">
          <s-number-field label="Latitude" name="latitude" id="console-edit-site-latitude" value="${ticketSite?.latitude ?? ""}" step="0.00001" style="flex:1;"></s-number-field>
          <s-number-field label="Longitude" name="longitude" id="console-edit-site-longitude" value="${ticketSite?.longitude ?? ""}" step="0.00001" style="flex:1;"></s-number-field>
        </s-stack>
        <s-stack direction="inline" gap="small" alignment="center">
          <s-button size="slim" id="btn-console-edit-site-capture-gps">Use Current GPS</s-button>
          <s-text id="console-edit-site-gps-status" tone="subdued" style="font-size:0.8rem;"></s-text>
        </s-stack>
        <div id="console-edit-site-error" style="display:none;color:var(--p-color-text-critical);font-size:0.875rem;"></div>
        <s-stack direction="inline" gap="small" style="justify-content:flex-end;">
          <s-button id="btn-save-site-details" variant="primary">Save Site Details</s-button>
          <s-button commandFor="console-edit-site-modal" command="--hide">Cancel</s-button>
        </s-stack>
      </s-stack>
    </s-modal>

    <s-modal id="console-edit-desc-modal" heading="Edit Ticket Description">
      <s-stack gap="base">
        <s-text tone="subdued">Update the failure description for this ticket.</s-text>
        <s-text-area
          id="console-edit-desc-input"
          label="Failure Description"
          rows="4"
          value="${escapeHtml(ticket.description || "")}"
        ></s-text-area>
        <div id="console-edit-desc-error" style="display:none;color:var(--p-color-text-critical);font-size:0.875rem;"></div>
        <s-stack direction="inline" gap="small" style="justify-content:flex-end;">
          <s-button id="btn-save-ticket-desc" variant="primary">Save Description</s-button>
          <s-button commandFor="console-edit-desc-modal" command="--hide">Cancel</s-button>
        </s-stack>
      </s-stack>
    </s-modal>

    <s-modal id="console-unsaved-work-modal" heading="Leave Without Saving?">
      <s-stack gap="base">
        <s-banner tone="warning" title="Unsaved technician work">
          <s-paragraph>Leaving now will discard your typed notes, meter entries, and uploaded photos.</s-paragraph>
        </s-banner>
        <s-text tone="subdued">Destination: <span id="console-unsaved-work-next-route"></span></s-text>
        <s-stack direction="inline" gap="small" style="justify-content:flex-end;">
          <s-button id="btn-console-leave-without-save" variant="critical">Leave</s-button>
          <s-button id="btn-console-stay-on-page" commandFor="console-unsaved-work-modal" command="--hide">Stay Here</s-button>
        </s-stack>
      </s-stack>
    </s-modal>
  `;
}
export function setupTicketConsolePageListeners() {
  const ticket = activeConsoleTicketSignal.peek();
  if (!ticket) return;

  configureConsoleAutocompletes(ticket);

  if (consoleBeforeUnloadHandler) {
    window.removeEventListener("beforeunload", consoleBeforeUnloadHandler);
  }
  consoleBeforeUnloadHandler = (e: BeforeUnloadEvent) => {
    if (!hasUnsavedConsoleWork()) return;
    e.preventDefault();
    e.returnValue = "";
  };
  window.addEventListener("beforeunload", consoleBeforeUnloadHandler);

  // Switch to Active Ticket listener
  const switchActiveTicketBtn = document.getElementById("btn-switch-to-active-ticket");
  if (switchActiveTicketBtn) {
    switchActiveTicketBtn.addEventListener("click", () => {
      const activeTid = switchActiveTicketBtn.getAttribute("data-ticket-id");
      if (activeTid) {
        window.location.hash = `#ticket-console?id=${activeTid}`;
      }
    });
  }

  const retrySessionSyncBtn = document.getElementById("btn-retry-session-sync");
  if (retrySessionSyncBtn) {
    retrySessionSyncBtn.addEventListener("click", () => void retryTicketConsoleSessionSync(ticket.id));
  }
  const returnToTicketsBtn = document.getElementById("btn-return-to-tickets");
  if (returnToTicketsBtn) {
    returnToTicketsBtn.addEventListener("click", () => { window.location.hash = "#tickets"; });
  }

  // 1. Back button click
  const backBtn = document.getElementById("console-back-btn");
  if (backBtn) {
    backBtn.addEventListener("click", () => {
      if (hasUnsavedConsoleWork()) {
        openConsoleLeavePrompt("#tickets");
        return;
      }
      window.location.hash = "#tickets";
    });
  }

  const suggestTicketBtn = document.getElementById("btn-suggest-ticket");
  if (suggestTicketBtn) {
    suggestTicketBtn.addEventListener("click", () => {
      const sourceSessionId = consoleSessionViewSignal.peek().activeSession?.id || consoleSessionViewSignal.peek().session?.id || "";
      const params = new URLSearchParams({ sourceTicketId: ticket.id });
      if (sourceSessionId) params.set("sourceSessionId", sourceSessionId);
      window.location.hash = `#ticket-suggestions?${params.toString()}`;
    });
  }

  const repairTabBtn = document.getElementById("tab-btn-repair");
  if (repairTabBtn) {
    repairTabBtn.addEventListener("click", () => {
      setConsoleActiveTab("repair");
    });
  }

  const closeoutTabBtn = document.getElementById("tab-btn-closeout");
  if (closeoutTabBtn) {
    closeoutTabBtn.addEventListener("click", () => {
      setConsoleActiveTab("closeout");
    });
  }

  // 2. Link printer asset modal trigger
  const openLinkBtn = document.getElementById("btn-open-link-asset");
  if (openLinkBtn) {
    openLinkBtn.addEventListener("click", () => {
      configureConsoleAutocompletes(ticket);
      const modal = document.getElementById("console-link-asset-modal") as { showOverlay?: () => void } | null;
      if (modal && typeof modal.showOverlay === "function") modal.showOverlay();
    });
  }

  // 3. Save linked asset to ticket (PATCH request)
  const saveLinkBtn = document.getElementById("btn-save-linked-asset");
  if (saveLinkBtn) {
    saveLinkBtn.addEventListener("click", async () => {
      const assetId = getOpmAutocomplete("select-link-asset")?.selectedId || "";
      const errorEl = document.getElementById("link-asset-error");
      if (errorEl) errorEl.style.display = "none";

      if (!assetId) {
        if (errorEl) {
          errorEl.textContent = "Please select a printer asset.";
          errorEl.style.display = "block";
        }
        return;
      }

      try {
        const res = await fetch(`/api/v1/tickets/${ticket.id}`, {
          method: "PATCH",
          headers: getAuthHeaders(),
          body: JSON.stringify({ assetId }),
        });

        if (res.ok) {
          const modal = document.getElementById("console-link-asset-modal") as { hideOverlay?: () => void } | null;
          if (modal && typeof modal.hideOverlay === "function")
            modal.hideOverlay();

          await refreshConsoleUIWithDraft(ticket.id);
        } else {
          const errData = await res.json();
          if (errorEl) {
            errorEl.textContent = errData.error || "Failed to link asset.";
            errorEl.style.display = "block";
          }
        }
      } catch (err) {
        console.error("Asset link failed:", err);
        if (errorEl) {
          errorEl.textContent = "Network failure linking asset.";
          errorEl.style.display = "block";
        }
      }
    });
  }

  // 4. Manual hours toggle
  const checkboxLogManual = document.getElementById(
    "checkbox-log-manual",
  ) as HTMLInputElement;
  if (checkboxLogManual) {
    checkboxLogManual.addEventListener("change", (e) => {
      const checked = (e.currentTarget as HTMLInputElement).checked;
      manualEntrySignal.value = checked;

      const stopwatchDisplay = document.getElementById("stopwatch-container");
      const timePickers = document.getElementById("manual-time-pickers");
      const closeoutForm = document.getElementById("visit-closeout-form");
      const inactiveBanner = document.getElementById("visit-inactive-banner");
      const durationDisplay = document.getElementById(
        "manual-duration-display",
      );

      if (stopwatchDisplay)
        stopwatchDisplay.style.display = checked ? "none" : "block";
      if (timePickers) timePickers.style.display = checked ? "block" : "none";
      if (durationDisplay)
        durationDisplay.style.display = checked ? "block" : "none";

      const shouldShowForm = activeConsoleSessionSignal.value?.status === "awaiting_closeout" || checked;
      if (closeoutForm)
        closeoutForm.style.display = shouldShowForm ? "block" : "none";
      if (inactiveBanner)
        inactiveBanner.style.display = shouldShowForm ? "none" : "block";

      syncConsoleTabState();

      // Reset validation state
      if (checked) {
        setConsoleActiveTab("closeout");
        validateManualTimeInputs();
      } else {
        setConsoleActiveTab("repair");
      }
      syncConsoleUnsavedState();
    });
  }

  // 5. Start/Stop session stopwatch trigger
  const toggleStopwatchBtn = document.getElementById("btn-toggle-stopwatch");
  if (toggleStopwatchBtn) {
    toggleStopwatchBtn.addEventListener("click", async () => {
      const currentSession = activeConsoleSessionSignal.value;
      if (currentSession?.status === "awaiting_closeout") return;

      toggleStopwatchBtn.setAttribute("loading", "true");
      toggleStopwatchBtn.setAttribute("disabled", "true");
      consoleSessionStateSignal.value = "submitting";
      try {
        if (currentSession?.status === "active") {
          const finishRes = await fetch(`/api/v1/tickets/${ticket.id}/sessions/${currentSession.id}/finish`, {
            method: "POST",
            headers: getAuthHeaders(),
          });
          if (!finishRes.ok) {
            if (finishRes.status === 409) {
              await retryTicketConsoleSessionSync(ticket.id);
              showToast(
                "The visit changed on the server. The console has been reconciled.",
                "warning",
              );
              return;
            }
            throw new Error(await getApiErrorMessage(finishRes, "Service time could not be finished."));
          }
          const finished = (await finishRes.json()) as ServiceSession;
          const elapsedSeconds = getSafeDurationSeconds(finished.startedAt, finished.endedAt);
          if (elapsedSeconds === null) {
            setConsoleSessionView({ state: "incomplete_session", session: finished, activeSession: null, elapsedSeconds: null, startedAtMs: parseSessionTimestamp(finished.startedAt), message: "Service time was finished but its timestamps are incomplete. Retry sync before closeout." });
          } else {
            setConsoleSessionView({ state: "awaiting_closeout", session: finished, activeSession: null, elapsedSeconds, startedAtMs: parseSessionTimestamp(finished.startedAt), message: null });
          }
          stopStopwatchLoop(false);
          setConsoleActiveTab("closeout");
          await refreshConsoleUIWithDraft(ticket.id);
          return;
        }

        if (!navigator.onLine) {
          consoleSessionStateSignal.value = "ready_no_session";
          throw new Error("A network connection is required to start a server-timed service session. Use manual visit logging while offline.");
        }

        const startRes = await fetch(`/api/v1/tickets/${ticket.id}/sessions/start`, {
          method: "POST",
          headers: getAuthHeaders(),
        });
        if (startRes.ok) {
          const started = (await startRes.json()) as ServiceSession;
          const startedAtMs = parseSessionTimestamp(started.startedAt);
          if (startedAtMs === null) {
            setConsoleSessionView({ state: "incomplete_session", session: started, activeSession: started, elapsedSeconds: null, startedAtMs: null, message: "The server started this visit without a valid start time. Retry session sync before continuing." });
          } else {
            setConsoleSessionView({ state: "active", session: started, activeSession: started, elapsedSeconds: 0, startedAtMs, message: null });
            startStopwatchLoop(startedAtMs);
          }
          await refreshConsoleUIWithDraft(ticket.id);
          return;
        }

        if (startRes.status === 409) {
          await retryTicketConsoleSessionSync(ticket.id);
          showToast("A current visit was found. The console has been reconciled with the server.", "warning");
          return;
        }
        throw new Error(await getApiErrorMessage(startRes, "Failed to start service session."));
      } catch (err) {
        if (currentSession?.status === "active") {
          setConsoleSessionView({ state: "unverified", session: currentSession, activeSession: currentSession, elapsedSeconds: null, startedAtMs: parseSessionTimestamp(currentSession.startedAt), message: "The session change could not be verified. Retry session sync before trying again." });
          await refreshConsoleUIWithDraft(ticket.id);
        } else {
          consoleSessionStateSignal.value = "ready_no_session";
        }
        showToast(err instanceof Error ? err.message : "Service session request failed.", "error");
      } finally {
        toggleStopwatchBtn.removeAttribute("loading");
        if (activeConsoleSessionSignal.value?.status !== "awaiting_closeout") {
          toggleStopwatchBtn.removeAttribute("disabled");
        }
      }
    });
  }

  const historyLoadMoreBtn = document.getElementById("btn-console-history-load-more");
  if (historyLoadMoreBtn) {
    historyLoadMoreBtn.addEventListener("click", () => {
      consoleHistoryVisibleCountSignal.value += 12;
      renderAssetHistoryTimeline();
    });
  }

  // 6. Consumables autocomplete population
  const consumablesAutocomplete = document.getElementById(
    "console-consumable-autocomplete",
  ) as any;
  if (consumablesAutocomplete) {
    const availableConsumables = consumablesSignal.value || [];
    const list = availableConsumables.map((c: any) => ({
      id: c.id,
      label: `${c.name} (Code: ${c.code}) - NRs. ${c.price || c.cost}`,
    }));
    consumablesAutocomplete.items = list;

    const updatePriceContext = () => {
      const contextEl = document.getElementById("console-part-price-context");
      if (!contextEl) return;

      if (!selectedConsumableId) {
        contextEl.textContent = "";
        return;
      }

      const consumable = availableConsumables.find(
        (c: any) => c.id === selectedConsumableId,
      );
      if (!consumable) {
        contextEl.textContent = "";
        return;
      }

      const qtyField = document.getElementById(
        "console-recommend-qty",
      ) as HTMLInputElement;
      const qty = qtyField ? Number(qtyField.value || 1) : 1;
      const price = consumable.price || consumable.cost || 0;
      const total = price * qty;

      contextEl.textContent = `NRs. ${price} each (Total: NRs. ${total})`;
    };

    consumablesAutocomplete.addEventListener("select", (e: CustomEvent) => {
      selectedConsumableId = e.detail.id;
      updatePriceContext();
    });

    const qtyInput = document.getElementById("console-recommend-qty");
    if (qtyInput) {
      qtyInput.addEventListener("input", updatePriceContext);
      qtyInput.addEventListener("change", updatePriceContext);
    }
  }

  // 7. Recommend parts button handler
  const addRecommendBtn = document.getElementById("btn-add-recommendation");
  if (addRecommendBtn) {
    addRecommendBtn.addEventListener("click", async () => {
      if (addRecommendBtn.hasAttribute("disabled")) return;

      const colloquialInput = document.getElementById("console-recommend-colloquial-name") as HTMLInputElement | null;
      const colloquialName = colloquialInput ? colloquialInput.value.trim() : "";
      if (!colloquialName) {
        showToast("Please enter a part description first.", "error");
        return;
      }

      const qtyField = document.getElementById("console-recommend-qty") as any;
      const quantity = qtyField ? Number(qtyField.value || 1) : 1;

      const scopeSelect = document.getElementById(
        "console-session-scope",
      ) as HTMLSelectElement;
      const scope = scopeSelect ? scopeSelect.value : "direct";

      let sessionId: string | null = null;
      if (scope === "active") {
        sessionId = activeConsoleSessionSignal.value?.id || null;
      } else if (scope !== "direct") {
        sessionId = scope;
      }

      addRecommendBtn.setAttribute("loading", "true");
      addRecommendBtn.setAttribute("disabled", "true");
      const originalLabel = addRecommendBtn.textContent || "Add Recommendation";
      addRecommendBtn.textContent = "Adding...";

      try {
        let res;
        if (sessionId) {
          res = await fetch(
            `/api/v1/tickets/sessions/${sessionId}/recommendations`,
            {
              method: "POST",
              headers: getAuthHeaders(),
              body: JSON.stringify({
                colloquialName,
                quantity,
              }),
            },
          );
        } else {
          res = await fetch(`/api/v1/tickets/${ticket.id}/recommendations`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({
              colloquialName,
              quantity,
            }),
          });
        }

        if (res.ok) {
          // Clear colloquial name input
          if (colloquialInput) {
            colloquialInput.value = "";
          }
          const qtyInput = document.getElementById(
            "console-recommend-qty",
          ) as any;
          if (qtyInput) qtyInput.value = "1";

          // Refresh recommendations
          await fetchRecommendations();
        } else {
          const errData = await res.json();
          showToast(errData.error || "Failed to recommend part.", "error");
        }
      } catch (err) {
        console.error(err);
        showToast("Network error recommending part.", "error");
      } finally {
        addRecommendBtn.removeAttribute("loading");
        addRecommendBtn.removeAttribute("disabled");
        addRecommendBtn.textContent = originalLabel;
      }
    });
  }

  // 8. Photo uploading click trigger
  const triggerUploadBtn = document.getElementById("btn-trigger-upload");
  const fileInput = document.getElementById(
    "console-photo-file",
  ) as HTMLInputElement;
  if (triggerUploadBtn && fileInput) {
    triggerUploadBtn.addEventListener("click", () => {
      fileInput.click();
    });

    fileInput.addEventListener("change", async () => {
      const file = (fileInput.files || [])[0];
      if (!file) return;

      // 1. Size check (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        showToast("File size exceeds the 10MB limit.", "error");
        fileInput.value = "";
        return;
      }

      triggerUploadBtn.setAttribute("loading", "true");
      triggerUploadBtn.setAttribute("disabled", "true");
      triggerUploadBtn.textContent = "Uploading (0%)...";

      try {
        // 2. Client-side image compression
        const compressedBlob = await compressImage(file);

        // 3. Upload with progress
        const data = await uploadPhotoWithProgress(
          compressedBlob,
          file.name,
          (percent) => {
            triggerUploadBtn.textContent = `Uploading (${percent}%)...`;
          },
        );

        uploadedPhotosSignal.value = [
          ...uploadedPhotosSignal.value,
          data.photoUrl,
        ];
        renderPhotoThumbnails();
      } catch (err: any) {
        console.error("Photo upload failed:", err);
        showToast(
          err.message || "Network failure during photo upload.",
          "error",
        );
      } finally {
        triggerUploadBtn.removeAttribute("loading");
        triggerUploadBtn.removeAttribute("disabled");
        triggerUploadBtn.textContent = "Upload Photo Evidence";
        fileInput.value = ""; // Reset uploader input
      }
    });
  }

  // 9. Submit stop session (with GPS capture)
  const submitBtn = document.getElementById("btn-submit-stop-session");
  if (submitBtn) {
    submitBtn.addEventListener("click", async () => {
      const isManual =
        manualEntrySignal.value &&
        activeConsoleSessionSignal.value?.status !== "awaiting_closeout";
      const errorBanner = document.getElementById("console-error-banner");
      const showError = (msg: string) => {
        if (errorBanner) {
          errorBanner.innerHTML = `
            <s-banner tone="critical" title="Validation Error">
              <s-paragraph>${escapeHtml(msg)}</s-paragraph>
            </s-banner>
          `;
          errorBanner.style.display = "block";
        }
      };

      if (errorBanner) {
        errorBanner.style.display = "none";
        errorBanner.innerHTML = "";
      }

      // Manual time validation check
      if (isManual) {
        const manualDateVal = (
          document.getElementById("input-manual-date") as HTMLInputElement
        )?.value;
        const manualStartVal = (
          document.getElementById("input-manual-start") as HTMLInputElement
        )?.value;
        const manualEndVal = (
          document.getElementById("input-manual-end") as HTMLInputElement
        )?.value;
        if (!manualDateVal || !manualStartVal || !manualEndVal) {
          showError(
            "Please select a valid date, start time, and end time for the manual visit log.",
          );
          return;
        }
        const startTime = new Date(`${manualDateVal}T${manualStartVal}`).getTime();
        const endTime = new Date(`${manualDateVal}T${manualEndVal}`).getTime();
        if (endTime <= startTime) {
          showError("End time must be after start time.");
          return;
        }
        if (endTime > Date.now()) {
          showError("End time cannot be in the future.");
          return;
        }
      }

      if (ticket.assetId && !runCountersValidation()) {
        showError(
          "Please correct validation errors in cumulative meter counters.",
        );
        return;
      }

      const counters: Record<string, number> = {};
      const pageTypes = pageTypesSignal.value || [];
      let hasCountersInput = false;

      if (ticket.assetId) {
        pageTypes.forEach((pt) => {
          const input = document.getElementById(
            `input-counter-${pt.id}`,
          ) as any;
          if (input && input.value) {
            counters[pt.id] = Number(input.value);
            hasCountersInput = true;
          }
        });
      }

      const mResetCheckbox = document.getElementById(
        "checkbox-maintenance-reset",
      ) as HTMLInputElement;
      const isMaintenanceReset = mResetCheckbox
        ? mResetCheckbox.checked
        : false;
      const summaryNotes =
        (document.getElementById("console-summary") as any)?.value || "";
      if (!summaryNotes) {
        showError("Technician Visit Notes/Summary is required.");
        return;
      }
      const isFinalCheckbox = document.getElementById(
        "checkbox-is-final",
      ) as HTMLInputElement;
      const isFinal = isFinalCheckbox ? isFinalCheckbox.checked : false;

      submitBtn.setAttribute("loading", "true");
      submitBtn.setAttribute("disabled", "true");

      try {
        const photoUrls = uploadedPhotosSignal.value;

        // Capture GPS coordinates (optional, only if missing and technician opted in)
        let lat: number | null = null;
        let lng: number | null = null;

        const captureGpsCheckbox = document.getElementById(
          "checkbox-capture-gps",
        ) as HTMLInputElement | null;
        const shouldCaptureGps = captureGpsCheckbox
          ? captureGpsCheckbox.checked
          : false;

        if (shouldCaptureGps && navigator.geolocation) {
          const gpsStatusEl = document.getElementById("console-gps-status");
          if (gpsStatusEl) {
            gpsStatusEl.innerHTML =
              '<s-text tone="subdued"><s-spinner size="small"></s-spinner> Capturing location…</s-text>';
          }
          try {
            await new Promise<void>((resolve) => {
              let resolved = false;
              const fallbackTimer = setTimeout(() => {
                if (!resolved) {
                  resolved = true;
                  console.warn("GPS capture timed out manually");
                  if (gpsStatusEl) {
                    gpsStatusEl.innerHTML =
                      '<s-badge tone="attention">Location Unavailable</s-badge> <s-text tone="subdued" style="font-size:0.8rem;margin-left:0.5rem;">GPS timed out.</s-text>';
                  }
                  resolve();
                }
              }, 10000);

              navigator.geolocation.getCurrentPosition(
                (position) => {
                  if (resolved) return;
                  resolved = true;
                  clearTimeout(fallbackTimer);
                  lat = position.coords.latitude;
                  lng = position.coords.longitude;
                  capturedGpsSignal.value = { lat, lng };
                  if (gpsStatusEl) {
                    gpsStatusEl.innerHTML = `<s-badge tone="success">Location Captured</s-badge> <s-text tone="subdued" style="font-size:0.8rem;margin-left:0.5rem;">${lat.toFixed(6)}, ${lng.toFixed(6)}</s-text>`;
                  }
                  resolve();
                },
                (gpsErr) => {
                  if (resolved) return;
                  resolved = true;
                  clearTimeout(fallbackTimer);
                  console.warn("GPS access denied or unavailable:", gpsErr);
                  capturedGpsSignal.value = null;
                  if (gpsStatusEl) {
                    gpsStatusEl.innerHTML =
                      '<s-badge tone="attention">Location Unavailable</s-badge> <s-text tone="subdued" style="font-size:0.8rem;margin-left:0.5rem;">GPS access denied or not supported.</s-text>';
                  }
                  resolve();
                },
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
              );
            });
          } catch (gpsSyncErr) {
            console.warn(
              "GPS capture threw synchronous exception:",
              gpsSyncErr,
            );
            capturedGpsSignal.value = null;
          }
        }

        const showOfflineWarningBanner = () => {
          const bannerContainer = document.getElementById(
            "console-banner-container",
          );
          if (bannerContainer) {
            bannerContainer.innerHTML = `
              <s-banner tone="warning" title="Offline Submission Queued">
                <s-stack gap="small">
                  <s-paragraph>You are currently offline or connection failed. Your visit closeout logs have been saved locally and will sync automatically when connection is restored.</s-paragraph>
                  <s-button size="slim" id="btn-retry-sync">Retry Sync Now</s-button>
                </s-stack>
              </s-banner>
            `;
            bannerContainer.style.display = "block";
            if (errorBanner) errorBanner.style.display = "none";

            const retryBtn = document.getElementById("btn-retry-sync");
            if (retryBtn) {
              retryBtn.addEventListener("click", async () => {
                retryBtn.setAttribute("loading", "true");
                const success = await processOfflineQueue();
                retryBtn.removeAttribute("loading");
                if (success) {
                  showToast("Offline requests synced successfully", "success");
                  bannerContainer.style.display = "none";
                  localStorage.removeItem(`opm_draft_${ticket.id}`);
                  window.location.hash = "#tickets";
                } else {
                  showToast(
                    "Sync failed. Please check your internet connection.",
                    "error",
                  );
                }
              });
            }
          }
        };

        if (!isManual) {
          let closeoutSession = activeConsoleSessionSignal.value;
          if (closeoutSession?.status === "active") {
            const finishRes = await fetch(`/api/v1/tickets/${ticket.id}/sessions/${closeoutSession.id}/finish`, {
              method: "POST",
              headers: getAuthHeaders(),
            });
            if (!finishRes.ok) {
              if (finishRes.status === 409) {
                await retryTicketConsoleSessionSync(ticket.id);
                showToast(
                  "The visit changed on the server. Review the reconciled state before submitting again.",
                  "warning",
                );
                return;
              }
              throw new Error(await getApiErrorMessage(finishRes, "Failed to finish service time on server."));
            }
            closeoutSession = (await finishRes.json()) as ServiceSession;
            activeConsoleSessionSignal.value = closeoutSession;
            stopStopwatchLoop(false);
          }

          if (closeoutSession?.status !== "awaiting_closeout") {
            throw new Error("No active or finished visit session found for closeout.");
          }
          if (!navigator.onLine) {
            throw new Error("A network connection is required to submit this visit log. Your draft remains saved locally.");
          }

          const closeoutUrl = `/api/v1/tickets/${ticket.id}/sessions/${closeoutSession.id}/closeout`;
          const closeoutPayload = {
            summary: summaryNotes,
            counters: hasCountersInput ? counters : undefined,
            isFinal,
            isMaintenanceReset,
            photoUrls,
            latitude: lat,
            longitude: lng,
          };
          const closeoutRes = await fetch(closeoutUrl, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(closeoutPayload),
          });
          if (!closeoutRes.ok) {
            const errData = await closeoutRes.json().catch(() => ({}));
            throw new Error(errData.message || errData.error || "Failed to submit visit log.");
          }
          const completedSession = await closeoutRes.json();
          localStorage.removeItem(`opm_draft_${ticket.id}`);
          activeConsoleSessionSignal.value = null;
          currentTechnicianSessionSignal.value = null;
          consoleSessionStateSignal.value = "ready_no_session";
          stopStopwatchLoop();
          showToast("Visit closeout submitted successfully", "success");
          showSuccessSummaryScreen(completedSession, false);
        } else {
          const manualDateVal = (
            document.getElementById("input-manual-date") as HTMLInputElement
          )?.value;
          const manualStartVal = (
            document.getElementById("input-manual-start") as HTMLInputElement
          )?.value;
          const manualEndVal = (
            document.getElementById("input-manual-end") as HTMLInputElement
          )?.value;

          if (!manualDateVal || !manualStartVal || !manualEndVal) {
            throw new Error(
              "Please select a valid date, start time, and end time for the manual visit log.",
            );
          }

          const manualUrl = `/api/v1/tickets/${ticket.id}/sessions`;
          const manualPayload = {
            startedAt: new Date(`${manualDateVal}T${manualStartVal}`).toISOString(),
            endedAt: new Date(`${manualDateVal}T${manualEndVal}`).toISOString(),
            summary: summaryNotes,
            counters: hasCountersInput ? counters : undefined,
            isFinal,
            isMaintenanceReset,
            photoUrls,
            latitude: lat,
            longitude: lng,
          };

          try {
            const res = await fetch(manualUrl, {
              method: "POST",
              headers: getAuthHeaders(),
              body: JSON.stringify(manualPayload),
            });

            if (res.ok) {
              const manualSess = await res.json();
              localStorage.removeItem(`opm_draft_${ticket.id}`);
              showToast("Manual visit logged successfully", "success");
              showSuccessSummaryScreen(manualSess, true);
            } else {
              const errData = await res.json().catch(() => ({}));
              const errMsg =
                errData.error ||
                errData.message ||
                "Failed to log manual visit.";
              showToast(errMsg, "error");
            }
          } catch (err: any) {
            console.warn(
              "API failure during manual log, queueing request offline:",
              err,
            );
            savePendingRequest(
              manualUrl,
              "POST",
              manualPayload,
              getAuthHeaders(),
            );
            showOfflineWarningBanner();
          }
        }
      } catch (err: any) {
        console.error(err);
        showError(err.message || "Error occurred during closeout submission.");
      } finally {
        submitBtn.removeAttribute("loading");
        submitBtn.removeAttribute("disabled");
      }
    });
  }

  const openEditDescBtn = document.getElementById("btn-open-edit-desc-modal");
  if (openEditDescBtn) {
    openEditDescBtn.addEventListener("click", () => {
      const input = document.getElementById("console-edit-desc-input") as HTMLTextAreaElement | null;
      if (input) input.value = ticket.description || "";
      openModal("console-edit-desc-modal");
    });
  }

  const saveDescBtn = document.getElementById("btn-save-ticket-desc");
  if (saveDescBtn) {
    saveDescBtn.addEventListener("click", async () => {
      const errorEl = document.getElementById("console-edit-desc-error");
      if (errorEl) {
        errorEl.style.display = "none";
        errorEl.textContent = "";
      }

      const input = document.getElementById("console-edit-desc-input") as HTMLTextAreaElement | null;
      const descVal = input ? input.value.trim() : "";

      saveDescBtn.setAttribute("loading", "true");
      saveDescBtn.setAttribute("disabled", "true");
      try {
        const res = await fetch(`/api/v1/tickets/${ticket.id}`, {
          method: "PATCH",
          headers: getAuthHeaders(),
          body: JSON.stringify({ description: descVal }),
        });

        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(errBody.error || errBody.message || "Failed to update description.");
        }

        if (activeConsoleTicketSignal.value) {
          activeConsoleTicketSignal.value = {
            ...activeConsoleTicketSignal.value,
            description: descVal,
          };
        }

        closeModal("console-edit-desc-modal");
        showToast("Ticket description updated.", "success");
        renderTicketConsolePage();
      } catch (err: any) {
        console.error("Failed to save description:", err);
        if (errorEl) {
          errorEl.style.display = "block";
          errorEl.textContent = err.message || "Error saving description.";
        }
      } finally {
        saveDescBtn.removeAttribute("loading");
        saveDescBtn.removeAttribute("disabled");
      }
    });
  }

  const openEditAssetBtn = document.getElementById("btn-open-edit-asset-modal");
  if (openEditAssetBtn) {
    openEditAssetBtn.addEventListener("click", () => {
      openModal("console-edit-asset-modal");
    });
  }

  const openEditSiteBtn = document.getElementById("btn-open-edit-site-modal");
  if (openEditSiteBtn) {
    openEditSiteBtn.addEventListener("click", () => {
      openModal("console-edit-site-modal");
    });
  }

  const siteGpsBtn = document.getElementById("btn-console-edit-site-capture-gps");
  if (siteGpsBtn) {
    siteGpsBtn.addEventListener("click", () => {
      const status = document.getElementById("console-edit-site-gps-status");
      if (!navigator.geolocation) {
        if (status) status.textContent = "Geolocation is not supported.";
        return;
      }
      siteGpsBtn.setAttribute("loading", "true");
      siteGpsBtn.setAttribute("disabled", "true");
      if (status) status.textContent = "Capturing GPS...";
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = document.getElementById("console-edit-site-latitude") as HTMLInputElement | null;
          const lng = document.getElementById("console-edit-site-longitude") as HTMLInputElement | null;
          if (lat) lat.value = position.coords.latitude.toFixed(6);
          if (lng) lng.value = position.coords.longitude.toFixed(6);
          if (status) status.textContent = "GPS captured.";
          siteGpsBtn.removeAttribute("loading");
          siteGpsBtn.removeAttribute("disabled");
        },
        () => {
          if (status) status.textContent = "GPS permission denied.";
          siteGpsBtn.removeAttribute("loading");
          siteGpsBtn.removeAttribute("disabled");
        },
        { timeout: 5000 },
      );
    });
  }

  const saveSiteBtn = document.getElementById("btn-save-site-details");
  if (saveSiteBtn && ticket.siteId) {
    saveSiteBtn.addEventListener("click", async () => {
      const errorEl = document.getElementById("console-edit-site-error");
      if (errorEl) {
        errorEl.style.display = "none";
        errorEl.textContent = "";
      }
      const name = (
        document.getElementById("console-edit-site-name") as HTMLInputElement | null
      )?.value.trim() || "";
      const latVal = (
        document.getElementById("console-edit-site-latitude") as HTMLInputElement | null
      )?.value.trim() || "";
      const lngVal = (
        document.getElementById("console-edit-site-longitude") as HTMLInputElement | null
      )?.value.trim() || "";

      if (!name) {
        if (errorEl) {
          errorEl.textContent = "Site name is required.";
          errorEl.style.display = "block";
        }
        return;
      }

      saveSiteBtn.setAttribute("loading", "true");
      saveSiteBtn.setAttribute("disabled", "true");
      try {
        const body: {
          customerId: string;
          name: string;
          latitude?: number;
          longitude?: number;
        } = {
          customerId: ticket.customerId,
          name,
        };
        if (latVal) body.latitude = Number(latVal);
        if (lngVal) body.longitude = Number(lngVal);

        const res = await fetch(`/api/v1/sites/${ticket.siteId}`, {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || "Failed to save site details.");
        }
        closeModal("console-edit-site-modal");
        await initAssetsSignals();
        await refreshConsoleUIWithDraft(ticket.id);
        showToast("Site details saved successfully.", "success");
      } catch (err: any) {
        if (errorEl) {
          errorEl.textContent = err.message || "Network failure saving site.";
          errorEl.style.display = "block";
        }
      } finally {
        saveSiteBtn.removeAttribute("loading");
        saveSiteBtn.removeAttribute("disabled");
      }
    });
  }

  // 10. Save Printer Details handler
  const savePrinterBtn = document.getElementById("btn-save-printer-details");
  if (savePrinterBtn) {
    savePrinterBtn.addEventListener("click", async () => {
      if (savePrinterBtn.hasAttribute("disabled")) return;
      savePrinterBtn.setAttribute("loading", "true");
      savePrinterBtn.setAttribute("disabled", "true");
      const feedbackEl = document.getElementById("console-override-feedback");
      if (feedbackEl) {
        feedbackEl.style.display = "none";
        feedbackEl.textContent = "";
      }

      try {
        const review = buildPrinterDetailsReview(ticket);
        if (!review) {
          if (feedbackEl) {
            feedbackEl.textContent = "No changes to save.";
            feedbackEl.style.display = "block";
            feedbackEl.style.color = "var(--p-color-text-subdued)";
          }
          return;
        }

        const res = await fetch(`/api/v1/tickets/${ticket.id}`, {
          method: "PATCH",
          headers: getAuthHeaders(),
          body: JSON.stringify(review.updates),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || "Failed to save printer details.");
        }

        activeConsoleTicketSignal.value = (await res.json()) as Ticket;
        await refreshAssets();
        closeModal("console-edit-asset-modal");
        showToast("Printer details saved successfully.", "success");
      } catch (err: any) {
        if (feedbackEl) {
          feedbackEl.textContent =
            err.message || "Network failure saving printer details.";
          feedbackEl.style.display = "block";
          feedbackEl.style.color = "var(--p-color-text-critical)";
        }
      } finally {
        savePrinterBtn.removeAttribute("loading");
        savePrinterBtn.removeAttribute("disabled");
      }
    });
  }

  const leaveWithoutSaveBtn = document.getElementById("btn-console-leave-without-save");
  if (leaveWithoutSaveBtn) {
    leaveWithoutSaveBtn.addEventListener("click", () => {
      const nextRoute = pendingConsoleNavigationRoute;
      closeModal("console-unsaved-work-modal");
      clearConsoleLeavePrompt();
      if (nextRoute) {
        _bypassUnsavedWork = true;
        window.location.hash = nextRoute;
      }
    });
  }

  const stayOnPageBtn = document.getElementById("btn-console-stay-on-page");
  if (stayOnPageBtn) {
    stayOnPageBtn.addEventListener("click", () => {
      closeModal("console-unsaved-work-modal");
      clearConsoleLeavePrompt();
    });
  }

  const unsavedWorkModal = document.getElementById("console-unsaved-work-modal");
  if (unsavedWorkModal) {
    unsavedWorkModal.addEventListener("close", () => {
      clearConsoleLeavePrompt();
    });
  }

  // 11. Manual time input validation listeners
  const manualDateInput = document.getElementById(
    "input-manual-date",
  ) as HTMLInputElement;
  const manualStartInput = document.getElementById(
    "input-manual-start",
  ) as HTMLInputElement;
  const manualEndInput = document.getElementById(
    "input-manual-end",
  ) as HTMLInputElement;
  if (manualDateInput) {
    manualDateInput.addEventListener("input", validateManualTimeInputs);
  }
  if (manualStartInput) {
    manualStartInput.addEventListener("input", validateManualTimeInputs);
  }
  if (manualEndInput) {
    manualEndInput.addEventListener("input", validateManualTimeInputs);
  }

  // 12. Modal workflow & Geolocation listeners for Assets & Locations

  renderAssetHistoryTimeline();

  const cancelRecommendationModal = document.getElementById(
    "confirm-cancel-recommendation-modal",
  );
  if (cancelRecommendationModal) {
    cancelRecommendationModal.addEventListener("close", () => {
      pendingRecommendationCancelId = null;
      const textEl = document.getElementById(
        "confirm-cancel-recommendation-text",
      );
      if (textEl) textEl.textContent = "";
    });
  }

  const confirmCancelRecommendationBtn = document.getElementById(
    "btn-confirm-cancel-recommendation",
  );
  if (confirmCancelRecommendationBtn) {
    confirmCancelRecommendationBtn.addEventListener("click", async () => {
      if (!pendingRecommendationCancelId) return;
      confirmCancelRecommendationBtn.setAttribute("loading", "true");
      confirmCancelRecommendationBtn.setAttribute("disabled", "true");
      try {
        const res = await fetch(
          `/api/v1/parts-recommendations/${pendingRecommendationCancelId}/approve`,
          {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({ action: "reject" }),
          },
        );
        if (res.ok) {
          await fetchRecommendations();
          closeModal("confirm-cancel-recommendation-modal");
          pendingRecommendationCancelId = null;
        } else {
          const err = await res.json();
          showToast(err.error || "Failed to cancel request.", "error");
        }
      } catch (e) {
        console.error(e);
        showToast("Network error canceling request.", "error");
      } finally {
        confirmCancelRecommendationBtn.removeAttribute("loading");
        confirmCancelRecommendationBtn.removeAttribute("disabled");
      }
    });
  }

  // Link Printer Asset -> Open Register Asset Modal
  const openConsoleRegAssetBtn = document.getElementById(
    "btn-open-console-register-asset",
  );
  if (openConsoleRegAssetBtn) {
    openConsoleRegAssetBtn.addEventListener("click", () => {
      const linkModal = document.getElementById(
        "console-link-asset-modal",
      ) as any;
      if (linkModal && typeof linkModal.hideOverlay === "function") {
        linkModal.hideOverlay();
      }
      const regModal = document.getElementById(
        "console-register-asset-modal",
      ) as any;
      const errDiv = document.getElementById("console-register-asset-error");
      if (errDiv) errDiv.style.display = "none";
      if (regModal && typeof regModal.showOverlay === "function") {
        regModal.showOverlay();
      }
    });
  }

  // Register Asset Cancel -> Return to Link Asset Modal
  const cancelRegAssetBtn = document.getElementById(
    "btn-console-cancel-register-asset",
  );
  if (cancelRegAssetBtn) {
    cancelRegAssetBtn.addEventListener("click", (e) => {
      e.preventDefault();
      const regModal = document.getElementById(
        "console-register-asset-modal",
      ) as any;
      if (regModal && typeof regModal.hideOverlay === "function") {
        regModal.hideOverlay();
      }
      const linkModal = document.getElementById(
        "console-link-asset-modal",
      ) as any;
      if (linkModal && typeof linkModal.showOverlay === "function") {
        linkModal.showOverlay();
      }
    });
  }

  const regModalEl = document.getElementById("console-register-asset-modal");
  if (regModalEl) {
    regModalEl.addEventListener("close", () => {
      const currentTicket = activeConsoleTicketSignal.peek();
      if (currentTicket && !currentTicket.assetId) {
        const linkModal = document.getElementById(
          "console-link-asset-modal",
        ) as any;
        if (linkModal && typeof linkModal.showOverlay === "function") {
          linkModal.showOverlay();
        }
      }
    });
  }

  const inlineCreateSiteCheckbox = document.getElementById(
    "checkbox-reg-create-site-inline",
  ) as HTMLInputElement | null;
  const inlineSiteFields = document.getElementById(
    "console-reg-inline-site-fields",
  );
  if (inlineCreateSiteCheckbox && inlineSiteFields) {
    inlineCreateSiteCheckbox.addEventListener("change", () => {
      inlineSiteFields.style.display = inlineCreateSiteCheckbox.checked
        ? "block"
        : "none";
    });
  }

  const inlineSiteGpsBtn = document.getElementById("btn-reg-site-capture-gps");
  if (inlineSiteGpsBtn) {
    inlineSiteGpsBtn.addEventListener("click", (e) => {
      e.preventDefault();
      const gpsStatus = document.getElementById("reg-site-gps-status");
      if (!navigator.geolocation) {
        if (gpsStatus) {
          gpsStatus.textContent = "Geolocation is not supported.";
        }
        return;
      }

      inlineSiteGpsBtn.setAttribute("loading", "true");
      inlineSiteGpsBtn.setAttribute("disabled", "true");
      if (gpsStatus) gpsStatus.textContent = "Capturing GPS...";

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latInput = document.getElementById(
            "input-reg-site-lat",
          ) as HTMLInputElement | null;
          const lngInput = document.getElementById(
            "input-reg-site-lng",
          ) as HTMLInputElement | null;
          if (latInput) latInput.value = position.coords.latitude.toFixed(6);
          if (lngInput) lngInput.value = position.coords.longitude.toFixed(6);
          if (gpsStatus) gpsStatus.textContent = "GPS captured.";
          inlineSiteGpsBtn.removeAttribute("loading");
          inlineSiteGpsBtn.removeAttribute("disabled");
        },
        () => {
          if (gpsStatus) gpsStatus.textContent = "GPS permission denied.";
          inlineSiteGpsBtn.removeAttribute("loading");
          inlineSiteGpsBtn.removeAttribute("disabled");
        },
        { timeout: 5000 },
      );
    });
  }

  const gpsBannerBtn = document.getElementById("btn-gps-capture-banner");
  if (gpsBannerBtn && ticket.assetId) {
    gpsBannerBtn.addEventListener("click", () => {
      const gpsStatus = document.getElementById("gps-banner-status");
      if (!navigator.geolocation) {
        if (gpsStatus) gpsStatus.textContent = "Geolocation is not supported.";
        return;
      }

      gpsBannerBtn.setAttribute("loading", "true");
      gpsBannerBtn.setAttribute("disabled", "true");
      if (gpsStatus) gpsStatus.textContent = "Capturing GPS...";

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;

            const res = await fetch(`/api/v1/assets/${ticket.assetId}`, {
              method: "PATCH",
              headers: getAuthHeaders(),
              body: JSON.stringify({
                latitude: lat,
                longitude: lng,
              }),
            });

            if (!res.ok) {
              const data = await res.json().catch(() => ({}));
              throw new Error(data.error || "Failed to save location.");
            }

            if (ticket.siteId) {
              const site = (sitesSignal.peek() || []).find(s => s.id === ticket.siteId);
              if (site) {
                await fetch(`/api/v1/sites/${ticket.siteId}`, {
                  method: "PUT",
                  headers: getAuthHeaders(),
                  body: JSON.stringify({
                    customerId: ticket.customerId,
                    name: site.name,
                    latitude: lat,
                    longitude: lng,
                  }),
                }).catch(() => {});
              }
            }

            await refreshAssets();

            const banner = document.getElementById(
              "console-gps-missing-banner-container",
            );
            if (banner) banner.style.display = "none";
            showToast(`Asset GPS captured: ${lat.toFixed(6)}, ${lng.toFixed(6)}`, "success");
            renderTicketConsolePage();
          } catch (err: any) {
            console.error(err);
            if (gpsStatus) {
              gpsStatus.textContent =
                err.message || "Network failure saving GPS.";
            }
            gpsBannerBtn.removeAttribute("loading");
            gpsBannerBtn.removeAttribute("disabled");
          }
        },
        (err) => {
          console.warn("GPS capture error:", err);
          if (gpsStatus) gpsStatus.textContent = "GPS permission denied or timed out.";
          gpsBannerBtn.removeAttribute("loading");
          gpsBannerBtn.removeAttribute("disabled");
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
      );
    });
  }

  // Submit Register Asset & Link to Ticket
  const submitRegAssetBtn = document.getElementById(
    "btn-console-submit-register-asset",
  );
  if (submitRegAssetBtn) {
    submitRegAssetBtn.addEventListener("click", async (e) => {
      e.preventDefault();
      const form = document.getElementById(
        "form-console-register-asset",
      ) as HTMLFormElement;
      if (!form) return;

      const serialNumber =
        (form.querySelector('[name="serialNumber"]') as HTMLInputElement)
          ?.value || "";
      let siteId =
        (form.querySelector('[name="siteId"]') as HTMLSelectElement)?.value ||
        "";
      const createSiteInline =
        (
          document.getElementById(
            "checkbox-reg-create-site-inline",
          ) as HTMLInputElement | null
        )?.checked || false;
      const modelId =
        (form.querySelector('[name="modelId"]') as HTMLSelectElement)?.value ||
        "";
      const ownershipType =
        (form.querySelector('[name="ownershipType"]') as HTMLSelectElement)
          ?.value || "";
      const intervalSeconds = Number(
        (form.querySelector('[name="intervalSeconds"]') as HTMLInputElement)
          ?.value || 3600,
      );
      const anomalySensitivityMultiplier = Number(
        (
          form.querySelector(
            '[name="anomalySensitivityMultiplier"]',
          ) as HTMLInputElement
        )?.value || 3.0,
      );
      const isMps =
        (form.querySelector('[name="isMps"]') as HTMLInputElement)?.checked ??
        true;

      const errDiv = document.getElementById("console-register-asset-error");
      if (errDiv) errDiv.style.display = "none";

      if (!serialNumber || (!siteId && !createSiteInline) || !modelId || !ownershipType) {
        if (errDiv) {
          errDiv.textContent = "All fields are required.";
          errDiv.style.display = "block";
        }
        return;
      }

      submitRegAssetBtn.setAttribute("loading", "true");
      submitRegAssetBtn.setAttribute("disabled", "true");

      try {
        if (createSiteInline) {
          const siteName =
            (
              document.getElementById(
                "input-reg-site-name",
              ) as HTMLInputElement | null
            )?.value.trim() || "";
          const latVal =
            (
              document.getElementById(
                "input-reg-site-lat",
              ) as HTMLInputElement | null
            )?.value.trim() || "";
          const lngVal =
            (
              document.getElementById(
                "input-reg-site-lng",
              ) as HTMLInputElement | null
            )?.value.trim() || "";

          if (!siteName) {
            throw new Error("New site name is required.");
          }

          const sitePayload: {
            customerId: string;
            name: string;
            latitude?: number;
            longitude?: number;
          } = {
            customerId: ticket.customerId,
            name: siteName,
          };
          if (latVal) sitePayload.latitude = Number(latVal);
          if (lngVal) sitePayload.longitude = Number(lngVal);

          const siteRes = await fetch("/api/v1/sites", {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(sitePayload),
          });

          if (!siteRes.ok) {
            const data = await siteRes.json().catch(() => ({}));
            throw new Error(data.error || "Failed to create site.");
          }

          const createdSite = (await siteRes.json()) as Site;
          siteId = createdSite.id;
          sitesSignal.value = [...sitesSignal.value, createdSite];
        }

        // 1. Create the Asset
        const assetRes = await fetch("/api/v1/assets", {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            serialNumber,
            siteId,
            modelId,
            ownershipType: isMps ? ownershipType : "owned",
            customerId: ticket.customerId,
            intervalSeconds,
            anomalySensitivityMultiplier,
            isMps,
          }),
        });

        if (!assetRes.ok) {
          const data = await assetRes.json();
          throw new Error(data.error || "Failed to register printer asset.");
        }

        const newAsset = await assetRes.json();

        // 2. Link asset to ticket
        const ticketRes = await fetch(`/api/v1/tickets/${ticket.id}`, {
          method: "PATCH",
          headers: getAuthHeaders(),
          body: JSON.stringify({ assetId: newAsset.id }),
        });

        if (!ticketRes.ok) {
          const data = await ticketRes.json();
          throw new Error(
            data.error ||
            "Asset registered, but failed to link it to the ticket.",
          );
        }

        // Close modal and refresh ticket console
        const regModal = document.getElementById(
          "console-register-asset-modal",
        ) as any;
        if (regModal && typeof regModal.hideOverlay === "function") {
          regModal.hideOverlay();
        }
        form.reset();

        await refreshConsoleUIWithDraft(ticket.id);
      } catch (err: any) {
        console.error(err);
        if (errDiv) {
          errDiv.textContent = err.message || "Network failure.";
          errDiv.style.display = "block";
        }
      } finally {
        submitRegAssetBtn.removeAttribute("loading");
        submitRegAssetBtn.removeAttribute("disabled");
      }
    });
  }

  // 12. Initial lists render
  renderRecommendationsList();
  renderPhotoThumbnails();

  // 13. Autosave draft listeners
  const summaryEl = document.getElementById("console-summary");
  if (summaryEl) {
    summaryEl.addEventListener("input", () => {
      saveConsoleDraft(ticket.id);
      updateCloseoutSubmitState();
    });
  }

  const isFinalEl = document.getElementById("checkbox-is-final");
  if (isFinalEl) {
    isFinalEl.addEventListener("change", () => saveConsoleDraft(ticket.id));
  }

  const manualEntryEl = document.getElementById("checkbox-log-manual");
  if (manualEntryEl) {
    manualEntryEl.addEventListener("change", () => saveConsoleDraft(ticket.id));
  }

  const manualDateEl = document.getElementById("input-manual-date");
  if (manualDateEl) {
    manualDateEl.addEventListener("input", () => {
      saveConsoleDraft(ticket.id);
      updateCloseoutSubmitState();
    });
  }

  const manualStartEl = document.getElementById("input-manual-start");
  if (manualStartEl) {
    manualStartEl.addEventListener("input", () => {
      saveConsoleDraft(ticket.id);
      updateCloseoutSubmitState();
    });
  }

  const manualEndEl = document.getElementById("input-manual-end");
  if (manualEndEl) {
    manualEndEl.addEventListener("input", () => {
      saveConsoleDraft(ticket.id);
      updateCloseoutSubmitState();
    });
  }

  updateCloseoutSubmitState();
  const pageTypes = pageTypesSignal.value || [];
  pageTypes.forEach((pt) => {
    const input = document.getElementById(`input-counter-${pt.id}`);
    if (input) {
      const validateAndSaveCounter = () => {
        saveConsoleDraft(ticket.id);
        runCountersValidation();
      };
      input.addEventListener("input", validateAndSaveCounter);
      input.addEventListener("change", validateAndSaveCounter);
    }
  });

  const mResetCheckbox = document.getElementById("checkbox-maintenance-reset");
  if (mResetCheckbox) {
    mResetCheckbox.addEventListener("change", () => {
      saveConsoleDraft(ticket.id);
      runCountersValidation();
    });
  }

  // 14. Load preserved console draft details
  loadConsoleDraft(ticket.id);
  syncConsoleTabState();

  // 15. Setup online sync listener
  window.removeEventListener("online", handleOnlineSync);
  window.addEventListener("online", handleOnlineSync);

  // 16. Attempt to process offline queue immediately on load if online
  if (navigator.onLine) {
    processOfflineQueue().catch((err) =>
      console.error("Failed to auto-process queue on load:", err),
    );
  }
}

// ─── Sub-renderers (imperative DOM insertions, immune to input form losses) ───

function renderRecommendationsList() {
  const container = document.getElementById("console-recommendations-list");
  if (!container) return;

  const list = recommendedPartsSignal.value || [];
  const consumables = consumablesSignal.value || [];
  const ticket = activeConsoleTicketSignal.peek();
  const userRole = userProfileSignal.peek()?.role;
  const asset = (assetsListSignal.value || []).find(
    (item: Asset) => item.id === ticket?.assetId,
  );
  const isRentedAsset = asset ? (asset.isMps !== false && asset.ownershipType === "rented") : false;
  const canCancelRecommendation =
    userRole === "superadmin" ||
    userRole === "admin" ||
    userRole === "manager";

  if (list.length === 0) {
    container.innerHTML = `<s-text tone="subdued" style="font-style:italic;">No parts recommended yet.</s-text>`;
    return;
  }

  container.innerHTML = `
    <s-stack gap="small">
      ${list
      .map((item) => {
        const consumable = consumables.find(
          (c) => c.id === item.consumableId,
        );
        const name = "Requested: " + escapeHtml(
          item.colloquialName || (consumable ? consumable.name : "Unknown Part"),
        );
        const code = escapeHtml(consumable ? consumable.code : "—");

        let contextLabel = "Direct Request";
        if (item.sessionId) {
          const activeSess = activeConsoleSessionSignal.value;
          if (activeSess && item.sessionId === activeSess.id) {
            contextLabel = "Requested during current active visit";
          } else {
            const historicalSess = consoleSessionsSignal.value.find(
              (s: ServiceSession) => s.id === item.sessionId,
            );
            if (historicalSess) {
              contextLabel = escapeHtml(
                `Requested during Visit on ${formatDateTime(historicalSess.startedAt)}`,
              );
            } else {
              contextLabel = "Requested during Visit session";
            }
          }
        }

        const isEditing = editingRecommendationIdSignal.value === item.id;
        const approvalDisplay = getRecommendationApprovalDisplay(
          item.status,
          isRentedAsset,
        );

        let qtyDisplay = `<s-text tone="subdued" style="font-size:0.8rem;">Code: ${code} · Qty: <strong>${item.quantity}</strong></s-text>`;
        if (isEditing) {
          qtyDisplay = `
        <div style="display:flex; align-items:center; gap:0.5rem; margin-top:0.25rem;">
          <s-badge tone="info">Editing</s-badge>
          <s-text tone="subdued" style="font-size:0.8rem;">Code: ${code} · Qty:</s-text>
          <input type="number" id="input-edit-qty-${item.id}" value="${item.quantity}" min="1" style="width:60px; padding:0.25rem; border:1px solid #c4cdd5; border-radius:4px;" />
          <s-button size="slim" variant="primary" id="btn-save-edit-qty-${item.id}">Save</s-button>
          <s-button size="slim" id="btn-cancel-edit-qty-${item.id}">Cancel</s-button>
        </div>
      `;
        }

        const isApprovedForInstall = isRentedAsset
          ? item.status === "admin_approved"
          : item.status === "manager_approved";

        const actionButtons = [];
        if (item.status === "requested" && !isEditing) {
          actionButtons.push(
            `<s-button size="slim" id="btn-edit-qty-${item.id}">Edit Qty</s-button>`,
          );
          if (canCancelRecommendation) {
            actionButtons.push(
              `<s-button size="slim" variant="plain" id="btn-cancel-rec-${item.id}" data-id="${item.id}" style="color:var(--p-color-text-critical);">Cancel Request</s-button>`,
            );
          }
        } else if (isApprovedForInstall) {
          actionButtons.push(
            `<s-button size="slim" variant="primary" id="btn-install-rec-${item.id}" data-id="${item.id}">Install Part</s-button>`,
          );
        }

        return `
          <div style="background:#f9fafb;padding:0.75rem;border-radius:6px;border:1px solid #e1e3e5;margin-bottom:0.25rem;">
            <s-stack gap="extraSmall">
              <div style="display:flex;justify-content:space-between;align-items:center;">
                <s-text style="font-size:0.875rem;font-weight:600;">${name}</s-text>
                <s-badge tone="${approvalDisplay.tone}">${escapeHtml(approvalDisplay.badge)}</s-badge>
              </div>
              ${qtyDisplay}
              ${approvalDisplay.subtext ? `<s-text tone="subdued" style="font-size:0.75rem;">${escapeHtml(approvalDisplay.subtext)}</s-text>` : ""}
              <s-text tone="subdued" style="font-size:0.75rem;font-style:italic;">${contextLabel}</s-text>
              ${actionButtons.length > 0
            ? `
                <div style="display:flex;gap:0.5rem;margin-top:0.5rem;justify-content:flex-end;">
                  ${actionButtons.join("")}
                </div>
              `
            : ""
          }
            </s-stack>
          </div>
        `;
      })
      .join("")}
      <div style="height: 3.5rem;"></div> <!-- Scroll spacing buffer for last card element -->
    </s-stack>
  `;

  // Bind actions
  list.forEach((item) => {
    const editBtn = document.getElementById(`btn-edit-qty-${item.id}`);
    if (editBtn) {
      editBtn.addEventListener("click", () => {
        editingRecommendationIdSignal.value = item.id;
        renderRecommendationsList();
      });
    }

    const cancelEditBtn = document.getElementById(
      `btn-cancel-edit-qty-${item.id}`,
    );
    if (cancelEditBtn) {
      cancelEditBtn.addEventListener("click", () => {
        editingRecommendationIdSignal.value = null;
        renderRecommendationsList();
      });
    }

    const saveEditBtn = document.getElementById(`btn-save-edit-qty-${item.id}`);
    if (saveEditBtn) {
      saveEditBtn.addEventListener("click", async () => {
        const input = document.getElementById(
          `input-edit-qty-${item.id}`,
        ) as HTMLInputElement;
        const newQty = input ? Number(input.value) : 0;
        if (newQty <= 0) {
          showToast("Quantity must be greater than 0.", "error");
          return;
        }

        saveEditBtn.setAttribute("loading", "true");
        saveEditBtn.setAttribute("disabled", "true");

        try {
          const res = await fetch(`/api/v1/parts-recommendations/${item.id}`, {
            method: "PATCH",
            headers: getAuthHeaders(),
            body: JSON.stringify({ quantity: newQty }),
          });

          if (res.ok) {
            editingRecommendationIdSignal.value = null;
            await fetchRecommendations();
            showToast("Quantity updated successfully.", "success");
          } else {
            const err = await res.json();
            showToast(err.error || "Failed to update quantity.", "error");
          }
        } catch (e) {
          console.error(e);
          showToast("Network error updating quantity.", "error");
        } finally {
          saveEditBtn.removeAttribute("loading");
          saveEditBtn.removeAttribute("disabled");
        }
      });
    }

    const cancelBtn = document.getElementById(`btn-cancel-rec-${item.id}`);
    if (cancelBtn) {
      cancelBtn.addEventListener("click", async () => {
        pendingRecommendationCancelId = item.id;
        const consumable = consumables.find((c) => c.id === item.consumableId);
        const textEl = document.getElementById(
          "confirm-cancel-recommendation-text",
        );
        if (textEl) {
          textEl.textContent = `Cancel ${consumable ? consumable.name : "this recommendation"
            }?`;
        }
        openModal("confirm-cancel-recommendation-modal");
      });
    }

    const installBtn = document.getElementById(`btn-install-rec-${item.id}`);
    if (installBtn) {
      installBtn.addEventListener("click", async () => {
        try {
          const res = await fetch(
            `/api/v1/parts-recommendations/${item.id}/approve`,
            {
              method: "POST",
              headers: getAuthHeaders(),
              body: JSON.stringify({ action: "install" }),
            },
          );
          if (res.ok) {
            await fetchRecommendations();
          } else {
            const err = await res.json();
            showToast(
              err.error || "Failed to mark part as installed.",
              "error",
            );
          }
        } catch (e) {
          console.error(e);
        }
      });
    }
  });
}

function renderPhotoThumbnails() {
  const container = document.getElementById("console-photo-thumbnails");
  if (!container) return;

  const urls = uploadedPhotosSignal.value || [];
  if (urls.length === 0) {
    container.innerHTML = "";
    return;
  }

  container.innerHTML = urls
    .map(
      (url, idx) => `
    <div style="position:relative; width:70px; height:70px; border:1px solid #c4cdd5; border-radius:4px; overflow:hidden;">
      <img src="${url}" style="width:100%; height:100%; object-fit:cover;" />
      <button id="btn-delete-photo-${idx}" style="
        position:absolute;
        top:2px;
        right:2px;
        background:rgba(215, 44, 13, 0.9);
        color:white;
        border:none;
        border-radius:50%;
        width:18px;
        height:18px;
        font-size:10px;
        line-height:18px;
        text-align:center;
        cursor:pointer;
        padding:0;
      ">×</button>
    </div>
  `,
    )
    .join("");

  // Bind removal button listeners
  urls.forEach((_, idx) => {
    const btn = document.getElementById(`btn-delete-photo-${idx}`);
    if (btn) {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        uploadedPhotosSignal.value = uploadedPhotosSignal.value.filter(
          (_, i) => i !== idx,
        );
        renderPhotoThumbnails();
      });
    }
  });
}

// ─── Manual Time Input Validation ──────────────────────────────────────────────

function validateManualTimeInputs() {
  const dateInput = document.getElementById(
    "input-manual-date",
  ) as HTMLInputElement;
  const startInput = document.getElementById(
    "input-manual-start",
  ) as HTMLInputElement;
  const endInput = document.getElementById(
    "input-manual-end",
  ) as HTMLInputElement;
  const durationDisplay = document.getElementById("manual-duration-display");
  const durationValue = document.getElementById("manual-duration-value");
  const durationError = document.getElementById("manual-duration-error");
  if (!dateInput || !startInput || !endInput) {
    updateCloseoutSubmitState();
    return;
  }

  const dateVal = dateInput.value;
  const startVal = startInput.value;
  const endVal = endInput.value;

  // Show duration display only if manual mode is active
  if (durationDisplay && manualEntrySignal.value) {
    durationDisplay.style.display = "block";
  }

  // Reset state
  if (durationError) {
    durationError.style.display = "none";
    durationError.textContent = "";
  }
  if (durationValue) {
    durationValue.textContent = "—";
  }

  if (!dateVal || !startVal || !endVal) {
    updateCloseoutSubmitState();
    return;
  }

  const startMs = new Date(`${dateVal}T${startVal}`).getTime();
  const endMs = new Date(`${dateVal}T${endVal}`).getTime();

  // Validate: end must be after start
  if (endMs <= startMs) {
    if (durationError) {
      durationError.textContent = "End time must be after start time.";
      durationError.style.display = "block";
    }
    if (durationValue) durationValue.textContent = "Invalid";
    updateCloseoutSubmitState();
    return;
  }

  // Validate: end cannot be in the future
  if (endMs > Date.now()) {
    if (durationError) {
      durationError.textContent = "End time cannot be set in the future.";
      durationError.style.display = "block";
    }
    if (durationValue) durationValue.textContent = "Invalid";
    updateCloseoutSubmitState();
    return;
  }

  // Calculate and display duration
  const diffMs = endMs - startMs;
  const diffSecs = Math.floor(diffMs / 1000);
  const hours = Math.floor(diffSecs / 3600);
  const minutes = Math.floor((diffSecs % 3600) / 60);
  const seconds = diffSecs % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? "s" : ""}`);
  if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? "s" : ""}`);
  if (seconds > 0 || parts.length === 0)
    parts.push(`${seconds} second${seconds !== 1 ? "s" : ""}`);

  if (durationValue) {
    durationValue.textContent = `Elapsed time: ${parts.join(" ")}`;
  }

  updateCloseoutSubmitState();
}

function updateCloseoutSubmitState(): void {
  const submitBtn = document.getElementById("btn-submit-stop-session");
  const requirements = document.getElementById("console-submit-requirements");
  if (!submitBtn || !requirements) return;

  const blockers: string[] = [];
  const session = activeConsoleSessionSignal.value;
  const isManual =
    manualEntrySignal.value && session?.status !== "awaiting_closeout";
  const summary = (document.getElementById("console-summary") as HTMLTextAreaElement | null)?.value.trim() || "";

  if (!isManual && session?.status !== "awaiting_closeout") {
    blockers.push("Finish service time before submitting the visit closeout.");
  }
  if (isManual) {
    const date = (document.getElementById("input-manual-date") as HTMLInputElement | null)?.value;
    const start = (document.getElementById("input-manual-start") as HTMLInputElement | null)?.value;
    const end = (document.getElementById("input-manual-end") as HTMLInputElement | null)?.value;
    if (!date || !start || !end) blockers.push("Enter the visit date, start time, and end time.");
    if (date && start && end) {
      const startMs = new Date(date + "T" + start).getTime();
      const endMs = new Date(date + "T" + end).getTime();
      if (endMs <= startMs) blockers.push("End time must be after start time.");
      if (endMs > Date.now()) blockers.push("End time cannot be in the future.");
    }
  }
  if (activeConsoleTicketSignal.value?.assetId && !runCountersValidation()) {
    blockers.push("Correct the meter counter values before submitting.");
  }
  if (!summary) blockers.push("Add technician visit notes before submitting.");
  if (!isManual && !navigator.onLine) {
    blockers.push("Reconnect to the server before submitting a live-session closeout.");
  }

  submitBtn.toggleAttribute("disabled", blockers.length > 0);
  if (blockers.length === 0) {
    requirements.style.display = "none";
    requirements.innerHTML = "";
    return;
  }
  const items = blockers.map((blocker) => "<s-list-item>" + escapeHtml(blocker) + "</s-list-item>").join("");
  requirements.innerHTML = "<s-banner tone=\"warning\" title=\"Complete these items before submitting\"><s-unordered-list>" + items + "</s-unordered-list></s-banner>";
  requirements.style.display = "block";
}
